import React, { useState, useEffect } from 'react';
import JqueryLoad from '../../../JqueryLoad/JqueryLoad';
import Hdashboard from '../../../components/Hdashboard';
import { AllTransactions, PaymentMadetoTeachers, RefundsListing, ewalletTotals, invoice_pdf, learnerList, paymentReceived, refferalCredits, teacherOrgList } from '../../../api/apiHandler';
import 'jspdf-autotable';
import Select from 'react-select';
import DataTable from 'react-data-table-component';
import { CButton, CFormInput, CInputGroup } from '@coreui/react';
import FontAwesome from 'react-fontawesome';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { el } from 'date-fns/locale';
import customStyles from '../../../utils/dataTableStyles';
import { Helmet } from 'react-helmet';
import useDebounce from '../../../utils/useDebounce';
import { formatAmount, roundAmount, toAmount } from '../../../utils/formatMoney';
import { downloadEwalletExcel } from '../../../utils/ewalletExcel';
import { withCode } from '../../../utils/dropdownLabel';

// A listing's learner is either a child profile (tbl_learner) or the account
// holder enrolled as themselves; the API tags each option with which one it is
// so the label can name the kind of account, the way the Teacher / Organization
// dropdown does.
const learnerRoleLabels = {
    learner: 'Learner',
    above_18: 'Adult',
    below_18: 'Parent',
    after_school: 'School'
};

// The balance cards are summed from the rows the filters select, so they follow
// the transaction-type dropdown as well: each tab maps to the leg(s) of the
// ledger it lists. "Credits" is the referral-credit ledger rather than a payment
// flow, so none of these payment totals apply to it and they read 0.00.
const totalsModeByFilter = {
    all: 'all',
    payments_received: 'received',
    payments_made: 'made',
    refunds: 'refunds',
    credits: 'credits'
};

// Which endpoint the Download button calls per tab, and any extra body it needs.
// One entry per transaction-type filter, each pointing at the SAME endpoint that
// fetched that tab's rows (fetchEwalletData) so the statement is the table on
// screen. `extraParams` carries what distinguishes two tabs sharing an endpoint:
// Refunds and Payments received are both paymentReceived, told apart by
// value:'refunded' — exactly as the listing does it.
const downloadRouteByFilter = {
    all: { fetchInvoice: AllTransactions, extraParams: {} },
    payments_received: { fetchInvoice: paymentReceived, extraParams: {} },
    payments_made: { fetchInvoice: PaymentMadetoTeachers, extraParams: {} },
    refunds: { fetchInvoice: paymentReceived, extraParams: { value: 'refunded' } },
    credits: { fetchInvoice: refferalCredits, extraParams: {} }
};

// Rows currently on screen per tab — the Download button is offered whenever the
// selected tab has something to render.
const rowsForFilter = (filterValue, data) => {
    switch (filterValue) {
        case 'all': return data.allData;
        case 'payments_received': return data.paymentReceivedData;
        case 'payments_made': return data.paymentMadeData;
        case 'refunds': return data.refundData;
        case 'credits': return data.creditsData;
        default: return [];
    }
};

// The "Total Received" card is the total of the "Net Paid (Excl. Tax)"
// column (computeEwalletTotals sums that exact figure), so the card and every
// instance of that column share ONE accent colour to show the admin where the
// card's number comes from: the dark shade on the card and on the column
// heading, the light tint of the same hue in the column's cells (which hold dark
// text). The pair is deliberately outside the currency palette used by the rest
// of the table (#dceefb for "In USD", #ffffe0 for "In CAD") — that is what makes
// the link readable; the column headings still say which currency they are.
const NET_PAID_ACCENT = '#0f6a6b';
const NET_PAID_ACCENT_TINT = '#d7eff0';

// Column ids the accent CSS targets. react-data-table-component renders them as
// data-column-id on both the heading and the cells, and a column's `style` key
// reaches the cells only — hence the heading is coloured from the stylesheet and
// the cells from this object.
const NET_PAID_COLUMN_ID_USD = 'net_paid_usd';
const NET_PAID_COLUMN_ID_CAD = 'net_paid_cad';
const netPaidCellStyle = { backgroundColor: NET_PAID_ACCENT_TINT };

// Second accent pair, exactly the same idea as NET_PAID_ACCENT above and
// deliberately a different hue from it: the "Total Tax Collected after
// deductions" card and the "Total Tax" column it is the total of. The two
// columns that column is worked out FROM — "Tax Price" and "Refund Tax Amount" —
// keep the ordinary currency palette, so only the card and the one column it
// sums wear this colour.
const TAX_ACCENT = '#8a4b00';
const TAX_ACCENT_TINT = '#fbe3c7';

const TOTAL_TAX_COLUMN_ID_USD = 'total_tax_usd';
const TOTAL_TAX_COLUMN_ID_CAD = 'total_tax_cad';
const taxCellStyle = { backgroundColor: TAX_ACCENT_TINT };

// Third accent pair, same idea again in a third hue: the "Total Refund Provided"
// card and the "Refund Amount" column it is the total of. Both are BEFORE TAX —
// the stored refund is tax-inclusive, and the tax part of it is reported by
// "Refund Tax Amount" instead (see refundWithoutTax / baseShareOfRefund).
const REFUND_ACCENT = '#8c2f4d';
const REFUND_ACCENT_TINT = '#f9dbe5';

const REFUND_COLUMN_ID_USD = 'refund_amount_usd';
const REFUND_COLUMN_ID_CAD = 'refund_amount_cad';
const refundCellStyle = { backgroundColor: REFUND_ACCENT_TINT };

// Fourth accent pair: the "Total Revenue" card and the "Admin Earnings After Deducting Fees"
// column it is the total of — the platform's own share of each sale, reduced in
// proportion by any refund (netEarning), LESS the actual Stripe fee the sale
// cost. Kept clearly greener than NET_PAID_ACCENT's teal so the two dark cards
// are not mistaken for each other.
const REVENUE_ACCENT = '#1e5c2f';
const REVENUE_ACCENT_TINT = '#cdebd4';

// The two columns the accent USED to sit on. Both ids are still needed — the
// columns are still there, still show the platform's refund-adjusted share, and
// are still what the "Admin Earnings After Deducting Fees" column is worked out from — they
// just render plain now, because the card is no longer their total.
const ADMIN_EARNING_COLUMN_ID_USD = 'admin_earning_usd';
const ADMIN_EARNING_COLUMN_ID_CAD = 'admin_earning_cad';

// NEW column: "Admin Earnings After Deducting Fees" — the "Admin Earning" cell less the "Fees"
// cell beside it, i.e. what the platform actually keeps on the row once Stripe
// has taken its cut. This is the accent-coloured column now, and the "Total
// Revenue" card is its exact total (computeEwalletTotals deducts
// total_stripe_fee_* from total_revenue_*), so adding this one column up by hand
// gives the number on the card.
const ADMIN_EARNING_AFTER_FEE_COLUMN_ID_USD = 'admin_earning_after_fee_usd';
const ADMIN_EARNING_AFTER_FEE_COLUMN_ID_CAD = 'admin_earning_after_fee_cad';
const adminEarningAfterFeeCellStyle = { backgroundColor: REVENUE_ACCENT_TINT };

// Previous version (kept for reference): the tint sat on the "Admin Earning"
// cells instead, through `style: adminEarningCellStyle` on the four Admin
// Earning columns —
//   const adminEarningCellStyle = { backgroundColor: REVENUE_ACCENT_TINT };

// "Fees" — the ACTUAL Stripe processing fee charged on the row's transaction.
//
// The figure is Stripe's own: the API records it per transaction from the
// charge's balance transaction into tbl_stripe_transaction_fee and serves it as
// stripe_fee_usd / stripe_fee_cad (api/modules/v1/wallet/stripe_fee.js). It is
// NEVER worked out here from a published rate — this page has no fee arithmetic
// in it at all, only the two fields the API sends.
//
// The column sits immediately after "Admin Earning", the platform's share this
// cost is set against, and immediately BEFORE "Admin Earnings After Deducting Fees", the
// subtraction of the two — so the three columns read left to right as the sum
// they are:
//
//     Admin Earning  -  Fees  =  Admin Earnings After Deducting Fees  ( = Total Revenue )
//
// It carries no accent colour of its own: no card is the total of it, it is the
// middle term. The accent belongs to the third column, which the "Total Revenue"
// card totals exactly (computeEwalletTotals deducts total_stripe_fee_* from
// total_revenue_*).
//
// OLD BEHAVIOUR (kept for reference) : the fee was reported ALONGSIDE Admin
// Earning without being taken out of anything — Total Revenue was the Admin
// Earning total exactly, and Admin Earning was the accent-coloured column. The
// gap that arrangement avoided (a card sitting below the column it totals, by
// exactly the fees, with nothing explaining why) is now shown on screen instead:
// the subtraction has its own column, and the card is that column's total.
//
// Only the tabs that list a CHARGE carry it: All Transactions, Payments received
// and Refunds. "Payments made" lists payouts, where Stripe's cut was already
// taken on the way in and is reported on the received row for the same charge,
// and "Credits" is the referral ledger, not a payment at all — a column of "-"
// on either would say nothing.
//
// It carries NO colour of its own — heading and cells both render plain, like any
// ordinary column. The two column ids below are still needed: they are what the
// download/Excel export and the "-" logic address the column by.
//
// Previous version — a mid-green of the revenue family on the heading
// (`const FEE_ACCENT = '#2f7d45'`, applied through the <style> block below) and a
// lighter tint of it on every cell (`const FEE_CELL_TINT = '#a8d8b4'`, applied
// through `feeCellStyle`), which together read as a solid green block down the
// table.

const FEE_COLUMN_ID_USD = 'stripe_fee_usd';
const FEE_COLUMN_ID_CAD = 'stripe_fee_cad';

export default function EWallet({ isLoader }) {

    const [data, setPaymentMadeData] = useState([]);
    console.log('data :', data);
    const [refundData, setRefundData] = useState([]);
    const [paymentReceivedData, setPaymentReceivedData] = useState([]);
    const [creditsData, setCreditsData] = useState([]);
    const [allData, setAllData] = useState([]);
    const [selectedPeriod, setSelectedPeriod] = useState('');
    const [selectedOption, setSelectedOption] = useState('');
    const [search, setSearch] = useState('');
    const debounceSearch = useDebounce(search, 750);
    const [selectedFilterOption, setSelectedFilterOption] = useState({
        value: "all",
        label: "All Transactions"
    });
    const navigate = useNavigate();

    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [currency, setCurrency] = useState('all');
    const [taxFilter, setTaxFilter] = useState('all');
    const [teacherOrgOptions, setTeacherOrgOptions] = useState([]);
    const [selectedTeacherOrg, setSelectedTeacherOrg] = useState(null);
    const [learnerOptions, setLearnerOptions] = useState([]);
    const [selectedLearner, setSelectedLearner] = useState(null);

    useEffect(() => {
        teacherOrgList().then((response) => {
            if (response.code === "1") {
                setTeacherOrgOptions(response.data.map((item) => ({
                    value: item.id,
                    // Code after the name — see withCode; it is the same code the
                    // listings show in their "To Code" column.
                    label: `${item.category === 'organization' ? 'Organization' : 'Teacher'}: ${withCode(item.full_name, item.code_generate)}`
                })));
            } else {
                setTeacherOrgOptions([]);
            }
        });

        learnerList().then((response) => {
            if (response.code === "1") {
                setLearnerOptions(response.data.map((item) => ({
                    // "<type>:<id>" — child learner ids and user ids overlap, so
                    // the value has to say which table it points at.
                    value: `${item.type}:${item.id}`,
                    // Code after the name — see withCode; it is the same code the
                    // listings show in their "From Code" column.
                    label: `${learnerRoleLabels[item.category] || 'Learner'}: ${withCode(item.full_name, item.code_generate)}`
                })));
            } else {
                setLearnerOptions([]);
            }
        });
    }, []);

    useEffect(() => {
        if (selectedFilterOption) {
            fetchEwalletData(selectedFilterOption.value);
        }
    }, [selectedFilterOption, debounceSearch, currency, selectedTeacherOrg, selectedLearner, taxFilter]);

    useEffect(() => {
        if (startDate && endDate) {
            fetchEwalletData(selectedFilterOption.value);
        }
    }, [startDate, endDate]);

    const fetchEwalletData = (selectedFilter) => {
        let requestData = {};

        if (startDate && endDate) {
            requestData = {
                startDate: startDate,
                endDate: endDate
            };
        }

        const requestDataWithSearch = { ...requestData, search: search };
        if (currency !== 'all') {
            requestDataWithSearch.currency = currency;
        }
        if (taxFilter !== 'all') {
            requestDataWithSearch.taxFilter = taxFilter;
        }
        if (selectedTeacherOrg) {
            requestDataWithSearch.teacherId = selectedTeacherOrg.value;
        }
        if (selectedLearner) {
            requestDataWithSearch.learnerId = selectedLearner.value;
        }

        if (selectedFilter === 'all') {
            isLoader(true)
            AllTransactions(requestDataWithSearch)
                .then((response) => {
                    console.log('AllTransactions :', response);
                    if (response.code === "1") {
                        const dataWithRowCount = response.data.map((row, index) => ({ ...row, id: index + 1 }));
                        setAllData(dataWithRowCount);
                        isLoader(false)
                    } else {
                        setAllData([]);
                        isLoader(false)
                    }
                })
                .catch((error) => {
                    setAllData([]);
                    isLoader(false)
                });

        } else if (selectedFilter === 'refunds') {
            isLoader(true)
            const updatedRequestData = {
                ...requestDataWithSearch,
                value: 'refunded'
            };
            paymentReceived(updatedRequestData)
                .then((response) => {
                    console.log('response: ', response);
                    if (response.code === "1") {
                        const dataWithRowCount = response.data.map((row, index) => ({ ...row, id: index + 1 }));
                        setRefundData(dataWithRowCount);
                        isLoader(false)
                    } else {
                        setRefundData([]);
                        isLoader(false)
                    }
                })
                .catch((error) => {
                    setRefundData([]);
                    isLoader(false)
                });

        } else if (selectedFilter === 'payments_made') {

            isLoader(true)
            PaymentMadetoTeachers(requestDataWithSearch)
                .then((response) => {
                    console.log('response: ', response);
                    if (response.code === "1") {
                        const dataWithRowCount = response.data.map((row, index) => ({ ...row, id: index + 1 }));
                        setPaymentMadeData(dataWithRowCount);
                        isLoader(false)
                    } else {
                        setPaymentMadeData([]);
                        isLoader(false)
                    }
                })
                .catch((error) => {
                    setPaymentMadeData([]);
                    isLoader(false)
                });

        } else if (selectedFilter === 'payments_received') {

            isLoader(true)
            paymentReceived(requestDataWithSearch)
                .then((response) => {
                    console.log('response :', response);
                    if (response.code === "1") {
                        const dataWithRowCount = response.data.map((row, index) => ({ ...row, id: index + 1 }));
                        setPaymentReceivedData(dataWithRowCount);
                        isLoader(false)
                    } else {
                        setPaymentReceivedData([]);
                        isLoader(false)
                    }
                })
                .catch((error) => {
                    setPaymentReceivedData([]);
                    isLoader(false)
                });

        } else if (selectedFilter === 'credits') {

            isLoader(true)
            refferalCredits(requestDataWithSearch)
                .then((response) => {
                    console.log('response: ', response);
                    if (response.code === "1") {
                        const dataWithRowCount = response.data.map((row, index) => ({ ...row, id: index + 1 }));
                        setCreditsData(dataWithRowCount);
                        isLoader(false)
                    } else {
                        setCreditsData([]);
                        isLoader(false)
                    }
                })
                .catch((error) => {
                    setCreditsData([]);
                    isLoader(false)
                });

        } else if (selectedFilter === 'aID') {

        } else if (selectedFilter === 'unaID') {

        } else if (selectedFilter === 'payment_hold') {

        } else if (selectedFilter === 'payment_under_review') {

        } else if (selectedFilter === 'payment_denied') {

        } else {
            setPaymentMadeData([]);
            setRefundData([]);
            setPaymentReceivedData([]);
            setAllData([]);
        }

    };

    const handleFilterChange = (selectedOption) => {
        setSelectedFilterOption(selectedOption);
        setStartDate("");
        setEndDate("");
    }

    const downloadPdf = async () => {
        try {
            isLoader(true)

            // The PDF renders whatever the page is currently showing, so every
            // filter is optional here — the date range included. Downloading a
            // learner's or a teacher's full history without picking a range is
            // a valid ask; the invoice header then reads "All Dates".
            const requestData = { downloadFlag: true };

            if (startDate && endDate) {
                requestData.startDate = startDate;
                requestData.endDate = endDate;
            }

            const requestDataWithSearch = { ...requestData, search: search };
            if (currency !== 'all') {
                requestDataWithSearch.currency = currency;
            }
            if (taxFilter !== 'all') {
                requestDataWithSearch.taxFilter = taxFilter;
            }
            if (selectedTeacherOrg) {
                requestDataWithSearch.teacherId = selectedTeacherOrg.value;
            }
            if (selectedLearner) {
                requestDataWithSearch.learnerId = selectedLearner.value;
            }

            // Every tab downloads the table it is actually showing, so the
            // download goes to the SAME endpoint with the SAME body that fetched
            // the rows on screen — see fetchEwalletData, which this mirrors.
            // Sending the wrong one is how the Refunds tab used to download the
            // Payments-received rows: paymentReceived without value:'refunded'
            // is the received listing.
            const { fetchInvoice, extraParams } = downloadRouteByFilter[selectedFilterOption.value]
                || downloadRouteByFilter.all;

            const response = await fetchInvoice({ ...requestDataWithSearch, ...extraParams });
            isLoader(false)
            if (response.code === "1" && response.data) {
                const pdfUrl = response.data;

                const pdfResponse = await fetch(pdfUrl);
                const pdfBlob = await pdfResponse.blob();

                const randomName = `invoice_${Date.now()}.pdf`;

                const pdfFile = new File([pdfBlob], randomName, { type: 'application/pdf' });

                const pdfDataUrl = URL.createObjectURL(pdfFile);

                const downloadLink = document.createElement('a');
                downloadLink.href = pdfDataUrl;
                downloadLink.download = randomName;

                downloadLink.click();
            } else {
                console.error('Invalid response or PDF URL not found.');
            }
        } catch (error) {
            isLoader(false)
            console.error('Error downloading PDF:', error);
        }
    }

    // -------------------------------------------------------------------------
    // Refund split (amount vs tax), "Net Paid (Excl. Tax)" and the
    // net-based earnings. Same rules as the Teacher / Organization E-Wallet
    // pages, so one transaction reads the same figure on every screen.
    //
    // Every helper takes the amounts already on the row, in ONE currency — this
    // page renders a full USD block and a full CAD block side by side, so each
    // column passes its own currency's fields.
    //
    // `grossWithTax` is the "Class Price After Tax" value, `tax` the "Tax Price"
    // value, so `gross - tax` is the pre-tax amount the learner paid (the same
    // number the "Class Price Before Tax" column shows).
    // -------------------------------------------------------------------------

    // A refund is STORED tax-inclusive: stripe_model writes
    // refund_amount = base + tax (per meeting: paid_amount + paid_tax_charge).
    // These two helpers split that stored amount back into its class part and its
    // tax part, so the refund can be reported without tax and the refunded tax can
    // be reported on its own. The split is APPORTIONED, not a flat subtraction, so
    // partial refunds stay correct:
    //     refund without tax = refund x ( base  / gross )
    //     refund tax         = refund x ( tax   / gross )   ( = refund - the above )
    //   full refund   -> exactly the pre-tax amount and exactly the tax
    //   half a refund -> half of each
    //   row with no tax / no breakdown to apportion -> all of it is class amount
    const refundWithoutTax = (grossWithTax, tax, refund) => {
        const gross = toAmount(grossWithTax);
        const base = Math.max(gross - toAmount(tax), 0);
        const refunded = toAmount(refund);
        if (refunded <= 0) return 0;
        return gross > 0
            ? Math.min(refunded * (base / gross), base)
            : Math.min(refunded, base);
    };

    // The tax that went back with the refund — the remainder of the same split, so
    // "refund without tax" + "refund tax" always adds back up to the stored refund.
    const refundTaxAmount = (grossWithTax, tax, refund) => {
        const refunded = toAmount(refund);
        if (refunded <= 0) return 0;
        return Math.max(refunded - refundWithoutTax(grossWithTax, tax, refund), 0);
    };

    // "Total Tax" — the tax the platform is left holding on the row, i.e. the two
    // columns beside it subtracted:
    //     Total Tax = ( Tax Price )  -  ( Refund Tax Amount )
    //   no refund      -> the whole tax that was charged
    //   full refund    -> 0.00
    //   partial refund -> the tax less that refund's tax part
    // The "Total Tax Collected after deductions" card is the total of this column
    // (computeEwalletTotals sums the same figure server-side), which is what the
    // accent colour the two share says. Floored at 0 so rounding can never show a
    // row giving back more tax than it charged.
    const netTaxRetained = (grossWithTax, tax, refund) => {
        return Math.max(toAmount(tax) - refundTaxAmount(grossWithTax, tax, refund), 0);
    };

    // "Net Paid (Excl. Tax)" — reported WITHOUT TAX, the same way the
    // "Refund Amount" column beside it is. Both sides of the subtraction are the
    // class amount only: the pre-tax amount the learner paid (the "Class Price
    // Before Tax" value, gross - tax) minus the class part of the refund
    // (refundWithoutTax, since the stored refund is tax-inclusive and its tax
    // share has to come back out first):
    //     net = ( class )  -  ( refund class part )
    //   no refund      -> exactly the pre-tax class amount
    //   full refund    -> 0.00
    //   partial refund -> class amount minus that refund's class part
    // The refunded TAX is not netted off here — it is reported on its own by the
    // "Refund Tax Amount" column, so no tax figure appears in this one.
    // Floored at 0 so a fully refunded row reads 0.00, never a negative.
    //
    // Previous TAX-INCLUSIVE version, kept for reference — it was the full charge
    // (class + tax) less the full tax-inclusive refund:
    // const netAmountPaidByLearner = (grossWithTax, tax, refund) =>
    //     Math.max(toAmount(grossWithTax) - toAmount(refund), 0);
    const netAmountPaidByLearner = (grossWithTax, tax, refund) => {
        const base = Math.max(toAmount(grossWithTax) - toAmount(tax), 0);
        return Math.max(base - refundWithoutTax(grossWithTax, tax, refund), 0);
    };

    // "Net Paid (Incl. Tax)" — the two columns before it added back
    // together, tax included again:
    //     ( Net Paid (Excl. Tax) )  +  ( Total Tax )
    // i.e. the whole tax-inclusive amount the learner is left having paid once the
    // refund has come off both the class part and the tax part. Added from the two
    // helpers rather than re-derived, so the column can never disagree with the
    // two it is made of. (It works out to charge - refund, which is what the
    // pre-tax split of this page replaced — see netAmountPaidByLearner above.)
    //   no refund      -> the full charge, tax included
    //   full refund    -> 0.00
    //   partial refund -> the charge less that refund, tax included in both
    const netPaymentWithTax = (grossWithTax, tax, refund) => {
        return (
            netAmountPaidByLearner(grossWithTax, tax, refund) +
            netTaxRetained(grossWithTax, tax, refund)
        );
    };

    // Teacher Earning AND Admin Earning both follow the net column instead of the
    // gross amount: they are earned on what the learner actually ended up paying,
    // so a refund reduces them with it. The split percentage is NOT re-invented
    // here — it still comes from the backend's teacher_earning / admin_earning
    // (which differ for In-Person vs Online); we only apply the share of the
    // payment the learner kept:
    //     earning = backend earning x ( net paid / amount charged )
    //   no refund      -> unchanged   |   full refund -> 0.00
    //   partial refund -> proportional
    // The ratio is taken tax-inclusive on BOTH sides — the charge the learner kept
    // over the charge they were billed — rather than from the tax-free net column
    // above, so that the column becoming tax-free does not change any earning.
    // The two ratios are the same number either way: taking the tax out of both
    // sides cancels, since a refund of half the tax-inclusive charge is a refund
    // of half the class amount. Earnings therefore stay the backend's share of the
    // CLASS price and never pick up any tax.
    // An amount charged of 0 (nothing to apportion) falls back to the earning.
    // `tax` is not read for that reason, but stays in the signature so every
    // helper on this page is called the same way and each call site still reads
    // which currency's tax the row belongs to.
    const netEarning = (earningAmount, grossWithTax, tax, refund) => {
        const earning = toAmount(earningAmount);
        const gross = toAmount(grossWithTax);
        if (gross <= 0) return earning;
        const keptWithTax = Math.max(gross - toAmount(refund), 0);
        return earning * (keptWithTax / gross);
    };

    // The All Transactions table mixes the received leg with the payout ("paid")
    // leg, where the money on the row IS the payout — the same expression its
    // "Class Price Before/After Tax" columns already use.
    const allTableGross = (row, currency) => currency === 'CAD'
        ? (row.payment_status === "paid" ? row.teacher_amount_cad : row.total_amount_cad)
        : (row.payment_status === "paid" ? row.teacher_amount_usd : row.total_amount_usd);

    // The "Fees" cell — the ACTUAL Stripe processing fee on the row's
    // transaction, exactly as the API recorded it (stripe_fee_usd /
    // stripe_fee_cad, from tbl_stripe_transaction_fee). There is deliberately no
    // arithmetic here: nothing on this page multiplies a rate, adds a fixed
    // charge or otherwise reconstructs the figure — if Stripe has not priced the
    // transaction, the cell says so rather than showing an estimate.
    //
    // "-" is rendered in three cases, all of which mean "not a fee this row
    // reports", never "0.00 of fee":
    //   * the row was charged in the OTHER currency (the same rule every money
    //     column on this page follows: a CAD-charged row is blank under "In USD")
    //   * the row is a PAYOUT (money going out; Stripe's cut was taken on the
    //     charge coming in and is reported on that row instead)
    //   * no actual fee is recorded yet for the transaction
    //     (stripe_fee_recorded = 0 — the charge has not settled at Stripe, or the
    //     backfill has not reached it. It fills in on a later load.)
    const isPayoutRow = (row) => row?.payment_status === 'paid';

    const feeCell = (row, currency) => {
        if (isPayoutRow(row)) return '-';
        if (!Number(row?.stripe_fee_recorded)) return '-';
        const isCadRow = row?.paid_currency === 'CAD';
        if (currency === 'CAD') {
            return isCadRow ? `$ ${formatAmount(row?.stripe_fee_cad)}` : '-';
        }
        return isCadRow ? '-' : `$ ${formatAmount(row?.stripe_fee_usd)}`;
    };

    // The fee as a NUMBER, for the subtraction below — the same figure feeCell
    // prints, with the three "-" cases counted as 0.00 rather than dashed out.
    // That is the right reading for a subtraction: "no fee on this row" (a
    // payout, the other currency, or a charge Stripe has not priced yet) means
    // nothing comes off the earning, never that the result is unknown. A fee is
    // NEVER estimated to fill the gap — if it is not recorded, it is not
    // deducted, exactly as the API totals it (computeEwalletTotals).
    const feeAmount = (row, currency) => {
        if (isPayoutRow(row)) return 0;
        if (!Number(row?.stripe_fee_recorded)) return 0;
        const isCadRow = row?.paid_currency === 'CAD';
        if (currency === 'CAD') return isCadRow ? toAmount(row?.stripe_fee_cad) : 0;
        return isCadRow ? 0 : toAmount(row?.stripe_fee_usd);
    };

    // "Admin Earnings After Deducting Fees" — what the platform actually keeps on the row:
    //
    //     netEarning( admin_earning )  -  the row's actual Stripe fee
    //
    // i.e. the "Admin Earning" cell less the "Fees" cell beside it, the two
    // columns it sits after. `gross` is passed in because the two tables that
    // carry this column read a different field for it: the received listing uses
    // total_amount_*, while All Transactions switches to the payout amount on a
    // payout row (allTableGross) — the same value each table already hands
    // netEarning for its Admin Earning column, so the subtraction can never be
    // taken from a different earning than the one displayed.
    //
    // Dashed out in the currency the row was NOT charged in, like every other
    // money column here.
    //
    // It CAN come out negative, and is left that way on purpose: Stripe keeps its
    // processing fee on a refunded charge, so a fully refunded sale earns 0.00 and
    // still costs the fee. Flooring it at 0.00 would hide a real loss and would
    // stop the column adding up to the "Total Revenue" card.
    const adminEarningAfterFee = (row, currency, gross) => {
        const earning = currency === 'CAD' ? row?.admin_earning_cad : row?.admin_earning_usd;
        const tax = currency === 'CAD' ? row?.tax_charge_cad : row?.tax_charge_usd;
        const refund = currency === 'CAD' ? row?.refund_amount_cad : row?.refund_amount_usd;
        return netEarning(earning, gross, tax, refund) - feeAmount(row, currency);
    };

    const adminEarningAfterFeeCell = (row, currency, gross) => {
        const isCadRow = row?.paid_currency === 'CAD';
        if (currency === 'CAD' ? !isCadRow : isCadRow) return '-';
        return `$ ${formatAmount(adminEarningAfterFee(row, currency, gross))}`;
    };


    // =====================================================================
    // Excel download — the same statement the "Download" button produces as a
    // PDF, handed over as a .xlsx instead.
    //
    // Every column below mirrors its PDF template in config/template1.js
    // (admin_ewallet_all_invoice, admin_ewallet_invoice,
    // admin_ewallet_paid_invoice, admin_ewallet_refunds_invoice,
    // admin_ewallet_credits_invoice) heading for heading, in the same order,
    // calling the same helper on the same field — those templates already mirror
    // the on-screen columns, so the sheet, the PDF and the table all report one
    // number per cell.
    //
    // The rows come straight from state: they are the rows the PDF renders (the
    // listing endpoints return the whole filtered set, and the download endpoint
    // re-runs the same query), so the file needs no round trip.
    // =====================================================================

    // The PDF prints "$ 12.34" in the currency a row was charged in and "-" in
    // the other one (ewUsdCell / ewCadCell). The sheet keeps the same rule but
    // emits a NUMBER so the column can be summed in Excel — downloadEwalletExcel
    // puts the "$" on with a cell format. See its header comment.
    const excelUsdCell = (row, value) => row?.paid_currency === 'CAD' ? '-' : roundAmount(value);
    const excelCadCell = (row, value) => row?.paid_currency === 'CAD' ? roundAmount(value) : '-';

    // The "Fees" cell for the sheet — the same actual Stripe fee the screen's
    // feeCell renders and the same three "-" cases (other currency, payout row,
    // no fee recorded yet), emitted as a NUMBER so the column can be summed in
    // Excel exactly like every other money column here.
    const excelFeeCell = (row, currency) => {
        if (isPayoutRow(row)) return '-';
        if (!Number(row?.stripe_fee_recorded)) return '-';
        return currency === 'CAD'
            ? excelCadCell(row, row?.stripe_fee_cad)
            : excelUsdCell(row, row?.stripe_fee_usd);
    };

    // The "Admin Earnings After Deducting Fees" cell for the sheet — the same subtraction the
    // screen's column shows (adminEarningAfterFee), emitted as a NUMBER so the
    // column the "Total Revenue" card totals can be added up in Excel too. Only
    // the "wrong currency" dash applies here: unlike the fee column beside it, a
    // payout row or an unpriced charge still HAS an earning to report, it simply
    // has nothing deducted from it.
    const excelAdminEarningAfterFeeCell = (row, currency, gross) => (currency === 'CAD'
        ? excelCadCell(row, adminEarningAfterFee(row, 'CAD', gross))
        : excelUsdCell(row, adminEarningAfterFee(row, 'USD', gross)));

    // The refund date, DD-MM-YYYY, dashed out when there is none — as the screen
    // and both refund statements format it.
    const excelRefundDate = (value) => {
        if (!value) return '-';
        const date = new Date(value);
        if (isNaN(date.getTime())) return '-';
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        return `${day}-${month}-${date.getFullYear()}`;
    };

    const excelLearnerAge = (row) => row?.learner_category === 'above_18'
        ? 'Adult'
        : (row?.learner_age ? row.learner_age : '-');

    // A party's name with their ACCOUNT CODE after it — "Alex Smith (2577)", the
    // same thing the PDF prints (config/template1.js `ewWithCode`).
    //
    // Names are not unique — two learners called "Alex Smith", or one parent's
    // two children, read identically in a "Learner's Name" column — so the code
    // is what tells an admin WHOSE rows a statement is showing. It is the same
    // code this page puts after a name in its Teacher / Organization and Learner
    // dropdowns (withCode) and shows in its "From Code" / "To Code" columns.
    //
    // A learner's code is the ACCOUNT HOLDER's (`usercode`), not tbl_learner's
    // own: every listing stamps a child profile's rows with the parent's code and
    // the Learner dropdown labels itself with that same field, so the two always
    // agree. A teacher or organization is `teachercode`. A row with a name but no
    // code prints the name alone (older accounts predate code generation), and a
    // row with no name at all still prints '-' — never a bare code, which would
    // read as a learner whose name happens to be a number.
    const excelPartyName = (name, code) => {
        const label = name === null || name === undefined ? '' : String(name).trim();
        if (!label) return '-';
        const value = code === null || code === undefined ? '' : String(code).trim();
        return value === '-' ? label : withCode(label, value);
    };

    // ---- "Class Date" / "Total Meetings" display rules ---------------------
    //
    // Written once and used by the on-screen tables AND the Excel sheets, so the
    // two can never render the same row differently.
    //
    // classDateText: a Multi-Day purchase is ONE charge covering a SPAN of
    // meetings, so it prints "first - last". Every other shape has a single
    // meeting and the API returns class_start_date === class_end_date, so it
    // prints that one date. The rule is on the DATES rather than on the class
    // type on purpose: the two listings that feed this page label class_type
    // differently ('multi_day' vs 'Multi-Day'), and comparing the span works for
    // both without either having to be normalised.
    //
    // totalMeetingsText: how many meetings the row is for. 0 / null means the
    // purchase has no meetings behind it (offline course, quiz, referral credit),
    // which reads as "-" rather than a misleading "0".
    const classDateText = (row) => {
        const start = row?.class_start_date;
        const end = row?.class_end_date;
        if (start && end && start !== end) return `${start} - ${end}`;
        return row?.class_date ? row.class_date : (start || '-');
    };

    const totalMeetingsText = (row) => {
        const total = Number(row?.total_meetings);
        return Number.isFinite(total) && total > 0 ? String(total) : '-';
    };

    // Columns shared by the four transaction statements — the learner and teacher
    // the row belongs to. Written once because all four print them identically.
    const excelPartyColumns = [
        { head: 'Learner’s Name', cell: (row) => excelPartyName(row?.learner_name, row?.usercode) },
        { head: 'Learner’s Age', cell: excelLearnerAge },
        { head: 'Learner’s Country', cell: (row) => row?.learner_country || '-' },
        { head: 'Learner’s Province', cell: (row) => row?.learner_state ? row.learner_state : '-' },
        { head: 'Teacher’s Country', cell: (row) => row?.teacher_country || '-' },
        { head: 'Teacher’s Province', cell: (row) => row?.teacher_state ? row.teacher_state : '-' },
    ];

    // ---- All Transactions: mirrors admin_ewallet_all_invoice ----
    //
    // This tab mixes the received leg, the payout leg and refunds, so From/To
    // flip with payment_status the way the PDF's switch does: money flows
    // buyer -> teacher on a sale, teacher -> buyer on a refund, and a payout
    // comes from the platform itself.
    const excelAllFromCode = (row) => row?.payment_status === 'refunded'
        ? (row?.teachercode || '-')
        : row?.payment_status === 'paid' ? '-' : (row?.usercode || '-');

    const excelAllFromName = (row) => row?.payment_status === 'refunded'
        ? row?.to_account
        : row?.payment_status === 'paid' ? 'StudentScholar' : row?.from_account;

    const excelAllToCode = (row) => row?.payment_status === 'refunded'
        ? (row?.usercode || '-')
        : (row?.teachercode || '-');

    const excelAllToName = (row) => {
        if (row?.payment_status === 'refunded') return row?.from_account;
        if (row?.payment_status === 'paid') {
            return row?.source === 'payment_paid_to_teachers' ? row?.teacher_name : row?.to_account;
        }
        return row?.to_account;
    };

    // "Class Price After Tax" and the pre-tax figure under it, per currency —
    // the payout leg's money IS the payout, which allTableGross encodes.
    const excelAllAfterTax = (row, currency) => allTableGross(row, currency);
    const excelAllBeforeTax = (row, currency) => toAmount(allTableGross(row, currency))
        - toAmount(currency === 'CAD' ? row?.tax_charge_cad : row?.tax_charge_usd);

    const excelAllColumns = [
        { head: 'No', cell: (row, index) => index + 1 },
        // { head: 'Date', cell: (row) => row?.enrolled_at || '-' },
        { head: 'Purchase Date', cell: (row) => row?.enrolled_at || '-' },
        // NEW: "Class Date" — the date of the class / meeting the row is about,
        // as distinct from the date beside it, which is when the money moved.
        // Supplied by the listing as `class_date`; "-" when the purchase has no
        // meeting behind it (offline course, quiz).
        // A Multi-Day row prints its span ("first - last"); see classDateText.
        // { head: 'Class Date', cell: (row) => row?.class_date ? row.class_date : '-' },
        { head: 'Class Date', cell: (row) => classDateText(row) },
        // NEW: "Total Meetings" — see totalMeetingsText.
        { head: 'Total Meetings', cell: (row) => totalMeetingsText(row) },
        { head: 'From Code', cell: excelAllFromCode },
        { head: 'From Name', cell: (row) => excelPartyName(excelAllFromName(row), excelAllFromCode(row)) },
        { head: 'To Code', cell: excelAllToCode },
        { head: 'To Name', cell: (row) => excelPartyName(excelAllToName(row), excelAllToCode(row)) },
        ...excelPartyColumns,
        { head: 'Program Name', cell: (row) => row?.activity_title ? row.activity_title : '-' },
        { head: 'Program Type', cell: (row) => row?.activity_type ? row.activity_type : '-' },
        { head: 'Platform Type', cell: (row) => row?.platform_type ? row.platform_type : 'Online' },
        { head: 'Class Type', cell: (row) => row?.class_type ? row.class_type : '-' },
        { head: 'Duration', cell: (row) => row?.duration ? row.duration : '-' },
        { head: 'Total Classes', cell: (row) => row?.total_classes ? row.total_classes : '-' },
        { head: 'Coupon Number', cell: (row) => row?.coupan_number ? row.coupan_number : '-' },

        // ---- USD block ----
        { head: 'Class Price In USD', money: true, cell: (row) => excelUsdCell(row, row?.class_price_usd || row?.class_price || 0) },
        // "Class Price Before Tax In USD" - commented out alongside the "Tax Price In
        // USD" column below it: with no USD tax to take off, it printed the very same
        // figure as the column after it, which now carries the tax-free title
        // "Total Class Price In USD".
        //
        // Kept verbatim so the column can be restored by deleting this comment.
        // { head: 'Class Price Before Tax In USD', money: true, cell: (row) => excelUsdCell(row, excelAllBeforeTax(row, 'USD')) },
        // "Tax Price In USD" - commented out: no tax is charged on a US sale, so the
        // column only ever printed $0.00. GST/HST is charged on the BUYER's province
        // (common.gst_hst_details reads the buyer's own state_id) and a buyer in a
        // Canadian province is billed in CAD, so every taxed row lands in the CAD
        // block instead, where "Tax Price In CAD" still reports it. Same treatment,
        // same reason, as the Earnings page's USD tax column.
        //
        // Kept verbatim so the column can be restored by deleting this comment.
        // { head: 'Tax Price In USD', money: true, cell: (row) => excelUsdCell(row, row?.tax_charge_usd) },
        // Titled without the tax wording: with no USD tax there is no "after tax"
        // for a USD row to be after. The cell is unchanged - only the heading is.
        // ("In CAD" keeps its Before/After Tax titles, where the tax is real.)
        { head: 'Total Class Price In USD', money: true, cell: (row) => excelUsdCell(row, excelAllAfterTax(row, 'USD')) },
        {
            head: 'Refund Amount In USD', money: true,
            cell: (row) => excelUsdCell(row, refundWithoutTax(excelAllAfterTax(row, 'USD'), row?.tax_charge_usd, row?.refund_amount_usd)),
        },
        {
            // "(Excl. Tax)" dropped from the USD heading - there is no USD tax for
            // the figure to be exclusive of. The CAD heading keeps it.
            head: 'Net Paid In USD', money: true,
            cell: (row) => excelUsdCell(row, netAmountPaidByLearner(excelAllAfterTax(row, 'USD'), row?.tax_charge_usd, row?.refund_amount_usd)),
        },
        {
            head: 'Teacher Earning In USD', money: true,
            cell: (row) => excelUsdCell(row, netEarning(row?.teacher_earning_usd, excelAllAfterTax(row, 'USD'), row?.tax_charge_usd, row?.refund_amount_usd)),
        },
        {
            head: 'Admin Earning In USD', money: true,
            cell: (row) => excelUsdCell(row, netEarning(row?.admin_earning_usd, excelAllAfterTax(row, 'USD'), row?.tax_charge_usd, row?.refund_amount_usd)),
        },
        // NEW column, mirroring the screen's "Fees in USD" — the actual Stripe
        // processing fee, straight after the Admin Earning it comes off.
        { head: 'Fees In USD', money: true, cell: (row) => excelFeeCell(row, 'USD') },
        // NEW column, mirroring the screen's "Admin Earnings After Deducting Fees in USD" — the
        // two columns above subtracted, and the column the "Total Revenue" card
        // at the foot of the sheet is the total of.
        {
            head: 'Admin Earnings After Deducting Fees in USD', money: true,
            cell: (row) => excelAdminEarningAfterFeeCell(row, 'USD', excelAllAfterTax(row, 'USD')),
        },

        // ---- CAD block ----
        { head: 'Class Price In CAD', money: true, cell: (row) => excelCadCell(row, row?.class_price_cad || 0) },
        { head: 'Class Price Before Tax In CAD', money: true, cell: (row) => excelCadCell(row, excelAllBeforeTax(row, 'CAD')) },
        { head: 'Tax Price In CAD', money: true, cell: (row) => excelCadCell(row, row?.tax_charge_cad) },
        { head: 'Class Price After Tax In CAD', money: true, cell: (row) => excelCadCell(row, excelAllAfterTax(row, 'CAD')) },
        {
            head: 'Refund Amount In CAD', money: true,
            cell: (row) => excelCadCell(row, refundWithoutTax(excelAllAfterTax(row, 'CAD'), row?.tax_charge_cad, row?.refund_amount_cad)),
        },
        {
            head: 'Refund Tax Amount In CAD', money: true,
            cell: (row) => excelCadCell(row, refundTaxAmount(excelAllAfterTax(row, 'CAD'), row?.tax_charge_cad, row?.refund_amount_cad)),
        },
        {
            head: 'Net Paid (Excl. Tax) In CAD', money: true,
            cell: (row) => excelCadCell(row, netAmountPaidByLearner(excelAllAfterTax(row, 'CAD'), row?.tax_charge_cad, row?.refund_amount_cad)),
        },
        {
            head: 'Total Tax In CAD', money: true,
            cell: (row) => excelCadCell(row, netTaxRetained(excelAllAfterTax(row, 'CAD'), row?.tax_charge_cad, row?.refund_amount_cad)),
        },
        {
            head: 'Net Paid (Incl. Tax) In CAD', money: true,
            cell: (row) => excelCadCell(row, netPaymentWithTax(excelAllAfterTax(row, 'CAD'), row?.tax_charge_cad, row?.refund_amount_cad)),
        },
        {
            head: 'Teacher Earning In CAD', money: true,
            cell: (row) => excelCadCell(row, netEarning(row?.teacher_earning_cad, excelAllAfterTax(row, 'CAD'), row?.tax_charge_cad, row?.refund_amount_cad)),
        },
        {
            head: 'Admin Earning In CAD', money: true,
            cell: (row) => excelCadCell(row, netEarning(row?.admin_earning_cad, excelAllAfterTax(row, 'CAD'), row?.tax_charge_cad, row?.refund_amount_cad)),
        },
        // NEW column, mirroring the screen's "Fees in CAD".
        { head: 'Fees In CAD', money: true, cell: (row) => excelFeeCell(row, 'CAD') },
        // NEW column, mirroring the screen's "Admin Earnings After Deducting Fees in CAD".
        {
            head: 'Admin Earnings After Deducting Fees in CAD', money: true,
            cell: (row) => excelAdminEarningAfterFeeCell(row, 'CAD', excelAllAfterTax(row, 'CAD')),
        },

        { head: 'Refund Date', cell: (row) => row?.payment_status === 'refunded' ? excelRefundDate(row?.refund_date) : '-' },
        { head: 'Status', cell: (row) => row?.payment_status || '-' },
        { head: 'Transaction ID', cell: (row) => row?.transaction_id || '-' },
    ];

    // ---- Payments received: mirrors admin_ewallet_invoice ----
    //
    // The received leg only, so the gross is total_amount_* on every row with no
    // payout rows to switch for. The CAD block keeps the screen's own order,
    // where Total Tax and Net Paid (Incl. Tax) sit ahead of Tax Price.
    const excelReceivedColumns = [
        { head: 'No', cell: (row, index) => index + 1 },
        { head: 'Payment Date', cell: (row) => row?.enrolled_at || '-' },
        // NEW: "Class Date" — the date of the class / meeting the row is about,
        // as distinct from the date beside it, which is when the money moved.
        // Supplied by the listing as `class_date`; "-" when the purchase has no
        // meeting behind it (offline course, quiz).
        // A Multi-Day row prints its span ("first - last"); see classDateText.
        // { head: 'Class Date', cell: (row) => row?.class_date ? row.class_date : '-' },
        { head: 'Class Date', cell: (row) => classDateText(row) },
        // NEW: "Total Meetings" — see totalMeetingsText.
        { head: 'Total Meetings', cell: (row) => totalMeetingsText(row) },
        { head: 'From Code', cell: (row) => row?.usercode ? row.usercode : '-' },
        { head: 'From Name', cell: (row) => excelPartyName(row?.from_account, row?.usercode) },
        { head: 'To Code', cell: (row) => row?.teachercode ? row.teachercode : '-' },
        { head: 'To Name', cell: (row) => excelPartyName(row?.to_account, row?.teachercode) },
        ...excelPartyColumns,
        { head: 'Program Name', cell: (row) => row?.activity_title || '-' },
        { head: 'Program Type', cell: (row) => row?.activity_type || '-' },
        { head: 'Platform Type', cell: (row) => row?.platform_type ? row.platform_type : 'Online' },
        { head: 'Coupon Number', cell: (row) => row?.coupan_number ? row.coupan_number : '-' },

        // ---- USD block ----
        { head: 'Class Price In USD', money: true, cell: (row) => excelUsdCell(row, row?.class_price_usd || row?.class_price || 0) },
        // "Class Price Before Tax In USD" - commented out alongside the "Tax Price In
        // USD" column below it: with no USD tax to take off, it printed the very same
        // figure as the column after it, which now carries the tax-free title
        // "Total Class Price In USD".
        //
        // Kept verbatim so the column can be restored by deleting this comment.
        // {
        //     head: 'Class Price Before Tax In USD', money: true,
        //     cell: (row) => excelUsdCell(row, toAmount(row?.total_amount_usd) - toAmount(row?.tax_charge_usd)),
        // },
        // "Tax Price In USD" - commented out: no tax is charged on a US sale, so the
        // column only ever printed $0.00. GST/HST is charged on the BUYER's province
        // (common.gst_hst_details reads the buyer's own state_id) and a buyer in a
        // Canadian province is billed in CAD, so every taxed row lands in the CAD
        // block instead, where "Tax Price In CAD" still reports it. Same treatment,
        // same reason, as the Earnings page's USD tax column.
        //
        // Kept verbatim so the column can be restored by deleting this comment.
        // { head: 'Tax Price In USD', money: true, cell: (row) => excelUsdCell(row, row?.tax_charge_usd) },
        // Titled without the tax wording - see the "All transactions" sheet above.
        { head: 'Total Class Price In USD', money: true, cell: (row) => excelUsdCell(row, row?.total_amount_usd) },
        {
            // "(Excl. Tax)" dropped from the USD heading - no USD tax to exclude.
            head: 'Net Paid In USD', money: true,
            cell: (row) => excelUsdCell(row, netAmountPaidByLearner(row?.total_amount_usd, row?.tax_charge_usd, row?.refund_amount_usd)),
        },
        {
            head: 'Teacher Earning In USD', money: true,
            cell: (row) => excelUsdCell(row, netEarning(row?.teacher_earning_usd, row?.total_amount_usd, row?.tax_charge_usd, row?.refund_amount_usd)),
        },
        {
            head: 'Admin Earning In USD', money: true,
            cell: (row) => excelUsdCell(row, netEarning(row?.admin_earning_usd, row?.total_amount_usd, row?.tax_charge_usd, row?.refund_amount_usd)),
        },
        // NEW column, mirroring the screen's "Fees In USD" on this tab.
        { head: 'Fees In USD', money: true, cell: (row) => excelFeeCell(row, 'USD') },
        // NEW column, mirroring the screen's "Admin Earnings After Deducting Fees in USD" — the
        // two columns above subtracted, and the column the "Total Revenue" card
        // at the foot of the sheet is the total of.
        {
            head: 'Admin Earnings After Deducting Fees in USD', money: true,
            cell: (row) => excelAdminEarningAfterFeeCell(row, 'USD', row?.total_amount_usd),
        },

        // ---- CAD block ----
        { head: 'Class Price In CAD', money: true, cell: (row) => excelCadCell(row, row?.class_price_cad || 0) },
        {
            head: 'Class Price Before Tax In CAD', money: true,
            cell: (row) => excelCadCell(row, toAmount(row?.total_amount_cad) - toAmount(row?.tax_charge_cad)),
        },
        {
            head: 'Total Tax In CAD', money: true,
            cell: (row) => excelCadCell(row, netTaxRetained(row?.total_amount_cad, row?.tax_charge_cad, row?.refund_amount_cad)),
        },
        {
            head: 'Net Paid (Incl. Tax) In CAD', money: true,
            cell: (row) => excelCadCell(row, netPaymentWithTax(row?.total_amount_cad, row?.tax_charge_cad, row?.refund_amount_cad)),
        },
        { head: 'Tax Price In CAD', money: true, cell: (row) => excelCadCell(row, row?.tax_charge_cad) },
        { head: 'Class Price After Tax In CAD', money: true, cell: (row) => excelCadCell(row, row?.total_amount_cad) },
        {
            head: 'Net Paid (Excl. Tax) In CAD', money: true,
            cell: (row) => excelCadCell(row, netAmountPaidByLearner(row?.total_amount_cad, row?.tax_charge_cad, row?.refund_amount_cad)),
        },
        {
            head: 'Teacher Earning In CAD', money: true,
            cell: (row) => excelCadCell(row, netEarning(row?.teacher_earning_cad, row?.total_amount_cad, row?.tax_charge_cad, row?.refund_amount_cad)),
        },
        {
            head: 'Admin Earning In CAD', money: true,
            cell: (row) => excelCadCell(row, netEarning(row?.admin_earning_cad, row?.total_amount_cad, row?.tax_charge_cad, row?.refund_amount_cad)),
        },
        // NEW column, mirroring the screen's "Fees In CAD" on this tab.
        { head: 'Fees In CAD', money: true, cell: (row) => excelFeeCell(row, 'CAD') },
        // NEW column, mirroring the screen's "Admin Earnings After Deducting Fees in CAD".
        {
            head: 'Admin Earnings After Deducting Fees in CAD', money: true,
            cell: (row) => excelAdminEarningAfterFeeCell(row, 'CAD', row?.total_amount_cad),
        },

        { head: 'Class Duration', cell: (row) => row?.duration || '-' },
        { head: 'Payment Status', cell: (row) => row?.payment_status || '-' },
        { head: 'Transaction ID', cell: (row) => row?.transaction_id || '-' },
    ];

    // ---- Payments made: mirrors admin_ewallet_paid_invoice ----
    //
    // The payout leg: the platform pays the teacher/organization, so there is no
    // learner payment, no refund and no earning split on the row — just the
    // amount that went out.
    const excelPaidColumns = [
        { head: 'No', cell: (row, index) => index + 1 },
        { head: 'From', cell: () => 'StudentScholar' },
        { head: 'To Code', cell: (row) => row?.teachercode == null ? '0000' : row.teachercode },
        { head: 'To Name', cell: (row) => excelPartyName(row?.teacher_name, row?.teachercode) },
        { head: 'Role', cell: (row) => row?.teacher_category || '-' },
        ...excelPartyColumns,
        { head: 'Program Name', cell: (row) => row?.activity_title ? row.activity_title : '-' },
        { head: 'Program Type', cell: (row) => row?.activity_type ? row.activity_type : '-' },
        { head: 'Platform Type', cell: (row) => row?.platform_type ? row.platform_type : '-' },
        { head: 'Class Type', cell: (row) => row?.class_type ? row.class_type : '-' },
        { head: 'Duration', cell: (row) => row?.duration ? row.duration : '-' },
        { head: 'Total Classes', cell: (row) => row?.total_classes ? row.total_classes : '-' },
        { head: 'Coupon Number', cell: (row) => row?.coupan_number ? row.coupan_number : '-' },
        { head: 'Class Price In USD', money: true, cell: (row) => excelUsdCell(row, row?.class_price_usd || 0) },
        { head: 'Amount In USD', money: true, cell: (row) => excelUsdCell(row, row?.teacher_amount_usd) },
        { head: 'Class Price In CAD', money: true, cell: (row) => excelCadCell(row, row?.class_price_cad || 0) },
        { head: 'Amount In CAD', money: true, cell: (row) => excelCadCell(row, row?.teacher_amount_cad) },
        { head: 'Payment Status', cell: (row) => row?.payment_status || '-' },
        { head: 'Payment Date', cell: (row) => row?.enrolled_at || '-' },
        // NEW: "Class Date" — the date of the class / meeting the row is about,
        // as distinct from the date beside it, which is when the money moved.
        // Supplied by the listing as `class_date`; "-" when the purchase has no
        // meeting behind it (offline course, quiz).
        // A Multi-Day row prints its span ("first - last"); see classDateText.
        // { head: 'Class Date', cell: (row) => row?.class_date ? row.class_date : '-' },
        { head: 'Class Date', cell: (row) => classDateText(row) },
        // NEW: "Total Meetings" — see totalMeetingsText.
        { head: 'Total Meetings', cell: (row) => totalMeetingsText(row) },
        { head: 'Transaction ID', cell: (row) => row?.transaction_id || '-' },
    ];

    // ---- Refunds: mirrors admin_ewallet_refunds_invoice ----
    //
    // The refunded rows of the received leg, so From is the teacher and To is the
    // buyer the money went back to. Transaction ID is the refund's own id.
    const excelRefundColumns = [
        { head: 'No', cell: (row, index) => index + 1 },
        { head: 'From Code', cell: (row) => row?.teachercode || '-' },
        { head: 'Teacher Name', cell: (row) => excelPartyName(row?.to_account, row?.teachercode) },
        { head: 'To Code', cell: (row) => row?.usercode || '-' },
        { head: 'User Name', cell: (row) => excelPartyName(row?.from_account, row?.usercode) },
        ...excelPartyColumns,

        { head: 'Program Name', cell: (row) => row?.activity_title || '-' },

        { head: 'Class Price In USD', money: true, cell: (row) => excelUsdCell(row, row?.class_price_usd || 0) },
        {
            head: 'Refund Amount In USD', money: true,
            cell: (row) => excelUsdCell(row, refundWithoutTax(row?.total_amount_usd, row?.tax_charge_usd, row?.refund_amount_usd)),
        },
        { head: 'Refund Date', cell: (row) => excelRefundDate(row?.refund_date) },
        {
            // "(Excl. Tax)" dropped from the USD heading - no USD tax to exclude.
            head: 'Net Paid In USD', money: true,
            cell: (row) => excelUsdCell(row, netAmountPaidByLearner(row?.total_amount_usd, row?.tax_charge_usd, row?.refund_amount_usd)),
        },
        // NEW column, mirroring the screen's "Fees In USD" on this tab — Stripe
        // keeps its processing fee on a refunded charge, so it is still reported.
        { head: 'Fees In USD', money: true, cell: (row) => excelFeeCell(row, 'USD') },

        { head: 'Class Price In CAD', money: true, cell: (row) => excelCadCell(row, row?.class_price_cad || 0) },
        {
            head: 'Refund Amount In CAD', money: true,
            cell: (row) => excelCadCell(row, refundWithoutTax(row?.total_amount_cad, row?.tax_charge_cad, row?.refund_amount_cad)),
        },
        {
            head: 'Refund Tax Amount In CAD', money: true,
            cell: (row) => excelCadCell(row, refundTaxAmount(row?.total_amount_cad, row?.tax_charge_cad, row?.refund_amount_cad)),
        },
        {
            head: 'Net Paid (Excl. Tax) In CAD', money: true,
            cell: (row) => excelCadCell(row, netAmountPaidByLearner(row?.total_amount_cad, row?.tax_charge_cad, row?.refund_amount_cad)),
        },
        // NEW column, mirroring the screen's "Fees In CAD" on this tab.
        { head: 'Fees In CAD', money: true, cell: (row) => excelFeeCell(row, 'CAD') },
        {
            head: 'Total Tax In CAD', money: true,
            cell: (row) => excelCadCell(row, netTaxRetained(row?.total_amount_cad, row?.tax_charge_cad, row?.refund_amount_cad)),
        },
        {
            head: 'Net Paid (Incl. Tax) In CAD', money: true,
            cell: (row) => excelCadCell(row, netPaymentWithTax(row?.total_amount_cad, row?.tax_charge_cad, row?.refund_amount_cad)),
        },
        { head: 'Program Type', cell: (row) => row?.activity_type || '-' },
        { head: 'Platform Type', cell: (row) => row?.platform_type || '-' },
        { head: 'Duration', cell: (row) => row?.duration || '-' },
        { head: 'Coupon Number', cell: (row) => row?.coupan_number ? row.coupan_number : '-' },
        { head: 'Refund Status', cell: (row) => row?.payment_status || '-' },
        // NEW: "Class Date" — the date of the class / meeting the row is about,
        // as distinct from the date beside it, which is when the money moved.
        // Supplied by the listing as `class_date`; "-" when the purchase has no
        // meeting behind it (offline course, quiz).
        // A Multi-Day row prints its span ("first - last"); see classDateText.
        // { head: 'Class Date', cell: (row) => row?.class_date ? row.class_date : '-' },
        { head: 'Class Date', cell: (row) => classDateText(row) },
        // NEW: "Total Meetings" — see totalMeetingsText.
        { head: 'Total Meetings', cell: (row) => totalMeetingsText(row) },
        { head: 'Transaction ID', cell: (row) => row?.refund_id || '-' },
    ];

    // ---- Credits: mirrors admin_ewallet_credits_invoice ----
    //
    // The referral-credit ledger, not a payment flow — both amounts are always
    // printed (there is no per-row paid currency to dash out), and the tab
    // carries no balance cards.
    const excelCreditsColumns = [
        { head: 'No', cell: (row, index) => index + 1 },
        // NEW: "Class Date" — the date of the class / meeting the row is about,
        // as distinct from the date beside it, which is when the money moved.
        // Supplied by the listing as `class_date`; "-" when the purchase has no
        // meeting behind it (offline course, quiz).
        // A Multi-Day row prints its span ("first - last"); see classDateText.
        // { head: 'Class Date', cell: (row) => row?.class_date ? row.class_date : '-' },
        { head: 'Class Date', cell: (row) => classDateText(row) },
        // NEW: "Total Meetings" — see totalMeetingsText.
        { head: 'Total Meetings', cell: (row) => totalMeetingsText(row) },
        { head: 'Referer', cell: (row) => row?.referer || '-' },
        { head: 'Referer Role', cell: (row) => row?.referer_role || '-' },
        { head: 'Referee', cell: (row) => row?.referee || '-' },
        { head: 'Referee Role', cell: (row) => row?.referee_role || '-' },
        { head: 'Amount In CAD', money: true, cell: (row) => roundAmount(row?.amount_cad) },
        { head: 'Amount In USD', money: true, cell: (row) => roundAmount(row?.amount) },
        { head: 'Referral Status', cell: (row) => (row?.is_valid === 1 ? 'Valid' : 'Invalid') },
    ];

    // One entry per transaction-type tab, the same shape downloadRouteByFilter
    // has: the columns to write, the file/sheet name, and whether the tab's
    // statement carries the balance cards (Credits does not — see above).
    const excelSheetByFilter = {
        all: { columns: excelAllColumns, sheet: 'All Transactions', file: 'all_transactions', withBalance: true },
        payments_received: { columns: excelReceivedColumns, sheet: 'Payments Received', file: 'payments_received', withBalance: true },
        payments_made: { columns: excelPaidColumns, sheet: 'Payments Made', file: 'payments_made', withBalance: true },
        refunds: { columns: excelRefundColumns, sheet: 'Refunds', file: 'refunds', withBalance: true },
        credits: { columns: excelCreditsColumns, sheet: 'Referral Credits', file: 'referral_credits', withBalance: false },
    };

    // The statement's heading and the filter line under it — the same two the
    // PDF prints (ewalletFilterSummary). Without a date range the heading is
    // just "STATEMENT" and the filter line says "All Dates", because downloading
    // an account's whole history is a valid ask.
    const excelStatementHeading = () => startDate && endDate
        ? `STATEMENT FOR: ${startDate} to ${endDate}`
        : 'STATEMENT';

    const excelFilterLines = () => {
        const parts = [];
        // The dropdown labels already read "Teacher: Jane Doe (SS123)" /
        // "Learner: Alex Smith (SS456)", so they go in as they are.
        if (selectedTeacherOrg) parts.push(selectedTeacherOrg.label);
        if (selectedLearner) parts.push(selectedLearner.label);
        if (!(startDate && endDate)) parts.push('All Dates');
        if (currency !== 'all') parts.push(`Currency: ${currency}`);
        if (taxFilter !== 'all') {
            parts.push(taxFilter === 'tax' ? 'Tax: Taxable only' : 'Tax: Non-taxable only');
        }
        if (search) parts.push(`Search: ${search}`);
        return parts.length ? [parts.join('  |  ')] : [];
    };

    // The four balance cards from the top of the page, reproduced under the table
    // exactly as the PDF reproduces them (ewalletBalanceSummary) — same cards,
    // same order, same three lines each, read from the same balanceData the cards
    // themselves render.
    // The tax card's two amounts, with a fallback for an API that predates the
    // split.
    //
    // No USD line: unlike its three neighbours, this card has no USD side to show.
    // GST/HST is only ever billed to a buyer in a Canadian province, and that buyer
    // is charged in CAD, so the figure was 0.00 in every dataset. total_tax_usd is
    // still SUMMED server-side, deliberately — if a taxed non-CAD row ever did
    // exist it would land in total_tax_cad_final but not in total_tax_cad, and the
    // two lines below would visibly disagree rather than the money going missing.
    //
    // total_tax_cad / _cad_final are newer than total_tax. An API still serving the
    // older shape returns total_tax alone, and formatAmount(undefined) is "0.00",
    // so without the fallback the card would quietly report NO TAX AT ALL rather
    // than the figure it has always shown — and frontend and API do not deploy
    // together, so that window is real. total_tax is the right stand-in for both
    // lines: it is every row's CAD mirror summed (computeEwalletTotals), i.e.
    // already the combined figure, which is the very reason total_tax_cad_final is
    // set from it server-side.
    const taxCardAmounts = () => ({
        cad: balanceData?.total_tax_cad ?? balanceData?.total_tax,
        final: balanceData?.total_tax_cad_final ?? balanceData?.total_tax,
    });

    const excelBalanceCards = () => {
        const splitCard = (label, usd, cad, final) => ({
            label,
            lines: [
                `USD Total: US$ ${formatAmount(usd)}`,
                `CAD Total: CAD$ ${formatAmount(cad)}`,
                `Final Total : CAD$ ${formatAmount(final)}`,
            ],
        });

        return [
            splitCard(
                'Total Received',
                balanceData?.total_received_usd,
                balanceData?.total_received_cad,
                balanceData?.total_received_cad_final,
            ),
            // This card prints no USD line (see taxCardAmounts), but the block is a
            // COLUMN GRID: downloadEwalletExcel fills a row's segments strictly
            // left to right, so dropping the first amount outright would slide this
            // card's CAD figure back under the column the other three use for USD —
            // the very misalignment the split fixed. The empty first line holds that
            // column open so CAD lines up under CAD and Final under Final.
            {
                label: 'Total Tax Collected after deductions',
                lines: [
                    '',
                    `CAD Total: CAD$ ${formatAmount(taxCardAmounts().cad)}`,
                    `Final Total : CAD$ ${formatAmount(taxCardAmounts().final)}`,
                ],
            },
            splitCard(
                'Total Refund Provided',
                balanceData?.total_refund_usd,
                balanceData?.total_refund_cad,
                balanceData?.total_refund_cad_final,
            ),
            splitCard(
                'Total Admin Earnings After Deducting Fees.',
                balanceData?.total_revenue_usd,
                balanceData?.total_revenue_cad,
                balanceData?.total_revenue_cad_final,
            ),
        ];
    };

    const downloadExcel = () => {
        const sheet = excelSheetByFilter[selectedFilterOption.value] || excelSheetByFilter.all;
        const rows = rowsForFilter(selectedFilterOption.value, {
            allData,
            paymentReceivedData,
            paymentMadeData: data,
            refundData,
            creditsData,
        });

        downloadEwalletExcel({
            fileName: `${sheet.file}_${Date.now()}`,
            sheetName: sheet.sheet,
            title: excelStatementHeading(),
            filterLines: excelFilterLines(),
            columns: sheet.columns,
            rows,
            balanceCards: sheet.withBalance ? excelBalanceCards() : [],
        });
    };

    const paymentMadeColumns = [
        {
            name: 'No',
            selector: row => row.id,
            sortable: false,
            width: "70px"
        },
        {
            name: 'From',
            cell: () => 'StudentScholar',
            sortable: true,
            width: '150px'
        },
        {
            name: 'To Code',
            selector: row => row.teachercode,
            sortable: true,
            width: "100px",
            cell: row => row.teachercode == null ? "0000" : row.teachercode
        },
        {
            name: 'To Name',
            selector: row => row.teacher_name,
            sortable: true,
            width: "150px",
            cell: row => (
                row.teacher_category == 'teacher' ? (
                    <span
                        onClick={() => navigate('/teacher_details', { state: { teacherId: row.teacher_id } })}
                        style={{ color: "rgb(87, 8, 97)", border: "none", background: "none", cursor: 'pointer' }}
                    >
                        {row.teacher_name}
                    </span>
                ) : row.teacher_category == 'organization' ? (
                    <span
                        onClick={() => navigate('/org_details', { state: { userId: row.teacher_id } })}
                        style={{ color: "rgb(87, 8, 97)", border: "none", background: "none", cursor: 'pointer' }}
                    >
                        {row.teacher_name}
                    </span>
                ) : (
                    <span>{row.teacher_name}</span>
                )
            )
        },
        {
            name: 'Role',
            selector: row => row.teacher_category,
            sortable: true,
            width: "100px"
        },
        {
            name: (
                <>
                    Learner <br /> Name
                </>
            ),
            selector: row => row.learner_name,
            cellExport: row => row.learner_name,
            width: '130px'
        },
        {
            name: (
                <>
                    Learner <br /> Age
                </>
            ),
            selector: row => row.learner_category === 'above_18' ? 'Adult' : (row.learner_age ? row.learner_age : '-'),
            cellExport: row => row.learner_category === 'above_18' ? 'Adult' : (row.learner_age ? row.learner_age : '-'),
            width: '100px'
        },
        {
            name: (
                <>
                    Learner <br /> Country
                </>
            ),
            selector: row => row.learner_country,
            cellExport: row => row.learner_country,
            width: '130px'
        },
        {
            name: (
                <>
                    Learner <br /> Province
                </>
            ),
            selector: row => row.learner_state ? row.learner_state : '-',
            cellExport: row => row.learner_state ? row.learner_state : '-',
            width: '130px',
        },
        {
            name: (
                <>
                    Teacher <br /> Country
                </>
            ),
            selector: row => row.teacher_country,
            cellExport: row => row.teacher_country,
            width: '130px'
        },
        {
            name: (
                <>
                    Teacher <br /> Province
                </>
            ),
            selector: row => row.teacher_state ? row.teacher_state : '-',
            cellExport: row => row.teacher_state ? row.teacher_state : '-',
            width: '130px',
        },
        {
            name: 'Program Name',
            selector: row => row.activity_title ? row.activity_title : '-',
            sortable: true,
            width: "180px"
        },
        {
            name: (
                <>
                    Program<br />Type
                </>
            ),
            selector: row => row.activity_type ? row.activity_type : "-",
            sortable: true,
            width: "100px"
        },
        {
            name: 'Platform Type',
            selector: row => row.platform_type ? row.platform_type : "-",
            sortable: true,
            width: "130px"
        },
        {
            name: (
                <>
                    Class<br />Type
                </>
            ),
            selector: row => row.class_type ? row.class_type : "-",
            sortable: true,
            width: "120px"
        },
        {
            name: 'Duration',
            selector: row => row.duration ? row.duration : "-",
            sortable: true,
            width: "155px"
        },
        {
            name: (
                <>
                    Total<br />Classes
                </>
            ),
            selector: row => row.total_classes ? row.total_classes : "-",
            sortable: true,
            width: "100px"
        },
        {
            name: 'Coupon Number',
            selector: row => row.coupan_number ? row.coupan_number : "-",
            sortable: true,
            width: "155px"
        },
        {
            name: (
                <>
                    Class Price<br />In USD
                </>
            ),
            selector: row => row.paid_currency === 'CAD' ? '-' : `$ ${formatAmount(row.class_price_usd || 0)}`,
            sortable: true,
            width: "100px",
            style: {
                backgroundColor: '#dceefb'
            }
        },
        {
            name: (
                <>
                    Amount<br />In USD
                </>
            ),
            selector: row => row.paid_currency === 'CAD' ? '-' : `$ ${formatAmount(row.teacher_amount_usd)}`,
            sortable: true,
            width: "100px",
            style: {
                backgroundColor: '#dceefb'
            }
        },
        {
            name: (
                <>
                    Class Price<br />In CAD
                </>
            ),
            selector: row => row.paid_currency === 'CAD' ? `$ ${formatAmount(row.class_price_cad || 0)}` : '-',
            sortable: true,
            width: "100px",
            style: {
                backgroundColor: '#ffffe0'
            }
        },
        {
            name: (
                <>
                    Amount<br />In CAD
                </>
            ),
            selector: row => row.paid_currency === 'CAD' ? `$ ${formatAmount(row.teacher_amount_cad)}` : '-',
            sortable: true,
            width: "100px",
            style: {
                backgroundColor: '#ffffe0'
            }
        },
        {
            name: (
                <>
                    Payment<br />Status
                </>
            ),
            selector: row => (
                <button className="bg-success border-0 text-light fw-500 px-1" style={{ cursor: "default" }}>
                    {row.payment_status}
                </button>
            ),
            sortable: true,
            width: "100px"
        },
        {
            name: (
                <>
                    Payment<br />Date
                </>
            ),
            // selector: row => {
            //     const date = new Date(row.created_at);
            //     const day = date.getDate();
            //     const month = date.getMonth() + 1;
            //     const formattedMonth = month < 10 ? `0${month}` : month;
            //     const formattedDay = day < 10 ? `0${day}` : day;
            //     const year = date.getFullYear();
            //     return `${formattedDay}-${formattedMonth}-${year}`;
            // },
            selector: row => row.enrolled_at,
            sortable: true,
            width: "150px"
        },
        // NEW: "Class Date" — the date of the CLASS / MEETING this row is
        // about, which is not the same thing as the payment date beside it: a
        // subscription paid for in January can be a session held in March.
        // For a payout row that settled one ongoing meeting it is that meeting's
        // date; for a one-off / multi-day purchase it is the first meeting of it.
        // The API supplies it as `class_date`, already dd/mm/YYYY. A row with no
        // meeting behind it (offline course, quiz) has no class date and shows
        // "-" — that is the data, not a lookup failure.
        {
            name: 'Class Date',
            // A Multi-Day row prints its span ("first - last") rather than one
            // date; see classDateText. Old single-date selector:
            // selector: row => row.class_date ? row.class_date : '-',
            // cellExport: row => row.class_date ? row.class_date : '-',
            selector: row => classDateText(row),
            cellExport: row => classDateText(row),
            sortable: true,
            // A Multi-Day row prints a RANGE here, which does not fit the width a
            // single date needed — react-data-table-component clipped it to
            // "17/08/2026 - _". Widened, and wrap:true so it breaks onto a second
            // line rather than being cut off if it still does not fit.
            wrap: true,
            // width: "130px"
            // width: "150px"
            width: "190px"
        },
        // NEW: "Total Meetings" — how many meetings this row is for. An ongoing
        // row is billed per meeting and counts 1; a One-Time / Multi-Day purchase
        // counts every meeting it bought. Rows with none (offline course, quiz,
        // referral credit) show "-". See totalMeetingsText.
        {
            name: 'Total Meetings',
            selector: row => totalMeetingsText(row),
            cellExport: row => totalMeetingsText(row),
            sortable: true,
            width: "130px"
        },
        {
            name: 'Transaction Id',
            selector: row => row.transaction_id,
            sortable: true,
            width: "250px"
        },
    ]

    const refundColumns = [
        {
            name: 'No',
            selector: row => row.id,
            sortable: false,
            width: "60px"
        },
        {
            name: 'From Code',
            selector: row => row.teachercode,
            sortable: false,
            width: "100px"
        },
        {
            name: 'Teacher Name',
            selector: row => row.to_account,
            sortable: true,
            width: "140px",
            cell: row => {

                if (row.teacher_category == 'teacher') {
                    return (
                        <span
                            onClick={() => navigate('/teacher_details', { state: { teacherId: row.teacher_id } })}
                            style={{ color: "rgb(87, 8, 97)", border: "none", background: "none", cursor: 'pointer' }}
                        >
                            {row.to_account}
                        </span>
                    )
                } else if (row.teacher_category == 'organization') {
                    return (
                        <span
                            onClick={() => navigate('/org_details', { state: { userId: row.teacher_id } })}
                            style={{ color: "rgb(87, 8, 97)", border: "none", background: "none", cursor: 'pointer' }}
                        >
                            {row.to_account}
                        </span>
                    )
                } else {
                    return (
                        <span
                            style={{ color: "rgb(87, 8, 97)", border: "none", background: "none" }}
                        >
                            {row.to_account}
                        </span>
                    );
                }
            }
        },
        {
            name: 'To Code',
            selector: row => row.usercode,
            sortable: false,
            width: "100px"
        },
        {
            name: 'User Name',
            selector: row => row.from_account,
            sortable: true,
            width: "140px",
            cell: row => (
                <span
                    onClick={() => navigate('/user_details', { state: { id: row.user_id } })}
                    style={{ color: "rgb(87, 8, 97)", border: "none", background: "none" }}
                >
                    {row.from_account}
                </span>
            )
        },
        {
            name: (
                <>
                    Learner <br /> Name
                </>
            ),
            selector: row => row.learner_name,
            cellExport: row => row.learner_name,
            width: '130px'
        },
        {
            name: (
                <>
                    Learner <br /> Age
                </>
            ),
            selector: row => row.learner_category === 'above_18' ? 'Adult' : (row.learner_age ? row.learner_age : '-'),
            cellExport: row => row.learner_category === 'above_18' ? 'Adult' : (row.learner_age ? row.learner_age : '-'),
            width: '100px'
        },
        {
            name: (
                <>
                    Learner <br /> Country
                </>
            ),
            selector: row => row.learner_country,
            cellExport: row => row.learner_country,
            width: '130px'
        },
        {
            name: (
                <>
                    Learner <br /> Province
                </>
            ),
            selector: row => row.learner_state ? row.learner_state : '-',
            cellExport: row => row.learner_state ? row.learner_state : '-',
            width: '130px',
        },
        {
            name: (
                <>
                    Teacher <br /> Country
                </>
            ),
            selector: row => row.teacher_country,
            cellExport: row => row.teacher_country,
            width: '130px'
        },
        {
            name: (
                <>
                    Teacher <br /> Province
                </>
            ),
            selector: row => row.teacher_state ? row.teacher_state : '-',
            cellExport: row => row.teacher_state ? row.teacher_state : '-',
            width: '130px',
        },
        // NEW column (added alongside the existing ones — nothing above/below changed)
        // Accent-coloured: this is the column the "Total Received" card totals.

        {
            name: 'Program Name',
            selector: row => row.activity_title,
            sortable: true,
            width: "170px"
        },

        {
            name: (
                <>
                    Class Price<br />In USD
                </>
            ),
            selector: row => row.paid_currency === 'CAD' ? '-' : `$ ${formatAmount(row.class_price_usd || 0)}`,
            sortable: true,
            width: "100px",
            style: {
                backgroundColor: '#dceefb'
            }
        },
        // Accent-coloured: this is the column the "Total Refund Provided" card totals.
        {
            id: REFUND_COLUMN_ID_USD,
            name: (
                <>
                    Refund Amount<br />In USD
                </>
            ),
            // Shown WITHOUT tax now — the stored refund_amount is tax-inclusive, and
            // the tax that went back with it has its own "Refund Tax Amount" column
            // below. See refundWithoutTax above. Previous tax-inclusive version:
            // selector: row => row.paid_currency === 'CAD' ? '-' : `$ ${formatAmount(row.refund_amount_usd || 0)}`,
            selector: row => row.paid_currency === 'CAD'
                ? '-'
                : `$ ${formatAmount(refundWithoutTax(row.total_amount_usd, row.tax_charge_usd, row.refund_amount_usd))}`,
            sortable: true,
            width: "100px",
            style: refundCellStyle
        },
        {
            name: 'Refund Date',
            selector: row => {
                const date = new Date(row.refund_date);
                const day = date.getDate();
                const month = date.getMonth() + 1;
                const formattedMonth = month < 10 ? `0${month}` : month;
                const formattedDay = day < 10 ? `0${day}` : day;
                const year = date.getFullYear();
                return `${formattedDay}-${formattedMonth}-${year}`;
            },
            sortable: true,
            width: "120px"
        },
        // NEW column: the tax part of the refund, reported on its own so the refund
        // amount above can stay tax-free. The two always add back up to the stored
        // refund_amount.
        // {
        //     name: (
        //         <>
        //             Refund Tax Amount<br />In USD
        //         </>
        //     ),
        //     selector: row => row.paid_currency === 'CAD'
        //         ? '-'
        //         : `$ ${formatAmount(refundTaxAmount(row.total_amount_usd, row.tax_charge_usd, row.refund_amount_usd))}`,
        //     sortable: true,
        //     width: "110px",
        //     style: {
        //         backgroundColor: '#dceefb'
        //     }
        // },
        // NEW column: the tax still held on the row (tax charged less refunded
        // tax) — accent-coloured, and the column the "Total Tax Collected after
        // deductions" card totals. See netTaxRetained.
        // {
        //     id: TOTAL_TAX_COLUMN_ID_USD,
        //     name: (
        //         <>
        //             Total Tax<br />In USD
        //         </>
        //     ),
        //     selector: row => row.paid_currency === 'CAD'
        //         ? '-'
        //         : `$ ${formatAmount(netTaxRetained(row.total_amount_usd, row.tax_charge_usd, row.refund_amount_usd))}`,
        //     sortable: true,
        //     width: "110px",
        //     style: taxCellStyle
        // },
        // NEW column (added alongside the existing ones — nothing above/below changed)
        // Accent-coloured: this is the column the "Total Received" card totals.
        {
            id: NET_PAID_COLUMN_ID_USD,
            name: (
                <>
                    Net Paid<br />In USD
                </>
            ),
            // Titled without "(Excl. Tax)": no tax is charged on a US sale, so
            // there is no USD tax for the figure to be exclusive of. The cell is
            // unchanged - only the heading is. ("In CAD" keeps the tax wording.)
            selector: row => row.paid_currency === 'CAD'
                ? '-'
                : `$ ${formatAmount(netAmountPaidByLearner(row.total_amount_usd, row.tax_charge_usd, row.refund_amount_usd))}`,
            sortable: true,
            width: "120px",
            style: netPaidCellStyle
        },
        // NEW column: the ACTUAL Stripe processing fee on this transaction — see
        // feeCell.
        //
        // It is shown on this tab too, even though a refunded sale gave the money
        // back: Stripe KEEPS its processing fee on a refunded charge, so the fee
        // is still a real cost of the transaction and still comes off the "Total
        // Revenue" card above (computeEwalletTotals deducts it without scaling it
        // by the refund). This column is what makes that visible on the tab where
        // it bites hardest.
        {
            id: FEE_COLUMN_ID_USD,
            name: (
                <>
                    Fees<br />In USD
                </>
            ),
            selector: row => feeCell(row, 'USD'),
            sortable: true,
            width: "120px"
        },
        {
            name: (
                <>
                    Class Price<br />In CAD
                </>
            ),
            selector: row => row.paid_currency === 'CAD' ? `$ ${formatAmount(row.class_price_cad || 0)}` : '-',
            sortable: true,
            width: "100px",
            style: {
                backgroundColor: '#ffffe0'
            }
        },
        // Accent-coloured: this is the column the "Total Refund Provided" card totals.
        {
            id: REFUND_COLUMN_ID_CAD,
            name: (
                <>
                    Refund Amount<br />In CAD
                </>
            ),
            // Shown WITHOUT tax now — see the USD column above and refundWithoutTax.
            // Previous tax-inclusive version:
            // selector: row => row.paid_currency === 'CAD' ? `$ ${formatAmount(row.refund_amount_cad || 0)}` : '-',
            selector: row => row.paid_currency === 'CAD'
                ? `$ ${formatAmount(refundWithoutTax(row.total_amount_cad, row.tax_charge_cad, row.refund_amount_cad))}`
                : '-',
            sortable: true,
            width: "100px",
            style: refundCellStyle
        },
        // NEW column: the tax part of the refund (CAD side of the pair above).
        {
            name: (
                <>
                    Refund Tax Amount<br />In CAD
                </>
            ),
            selector: row => row.paid_currency === 'CAD'
                ? `$ ${formatAmount(refundTaxAmount(row.total_amount_cad, row.tax_charge_cad, row.refund_amount_cad))}`
                : '-',
            sortable: true,
            width: "110px",
            style: {
                backgroundColor: '#ffffe0'
            }
        },
        // NEW column: the tax still held on the row (tax charged less the refunded
        // tax in the column above) — accent-coloured, and the column the "Total Tax
        // Collected after deductions" card totals. See netTaxRetained.
        {
            id: NET_PAID_COLUMN_ID_CAD,
            name: (
                <>
                    Net Paid (Excl. Tax)<br />In CAD
                </>
            ),
            selector: row => row.paid_currency === 'CAD'
                ? `$ ${formatAmount(netAmountPaidByLearner(row.total_amount_cad, row.tax_charge_cad, row.refund_amount_cad))}`
                : '-',
            sortable: true,
            width: "120px",
            style: netPaidCellStyle
        },
        // NEW column: the CAD side of the same actual Stripe fee — see feeCell.
        {
            id: FEE_COLUMN_ID_CAD,
            name: (
                <>
                    Fees<br />In CAD
                </>
            ),
            selector: row => feeCell(row, 'CAD'),
            sortable: true,
            width: "120px"
        },
        {
            id: TOTAL_TAX_COLUMN_ID_CAD,
            name: (
                <>
                    Total Tax<br />In CAD
                </>
            ),
            selector: row => row.paid_currency === 'CAD'
                ? `$ ${formatAmount(netTaxRetained(row.total_amount_cad, row.tax_charge_cad, row.refund_amount_cad))}`
                : '-',
            sortable: true,
            width: "110px",
            style: taxCellStyle
        },
        // NEW column: "Net Paid (Excl. Tax)" + "Total Tax" — the whole
        // tax-inclusive amount left with the platform for the row. See
        // netPaymentWithTax. No accent colour: no balance card totals it, and the
        // two accents on this page each mark one card and the one column it sums.
        // USD counterpart below, commented out like the other USD tax columns.
        // {
        //     name: (
        //         <>
        //             Net Paid (Incl. Tax)<br />In USD
        //         </>
        //     ),
        //     selector: row => row.paid_currency === 'CAD'
        //         ? '-'
        //         : `$ ${formatAmount(netPaymentWithTax(row.total_amount_usd, row.tax_charge_usd, row.refund_amount_usd))}`,
        //     sortable: true,
        //     width: "130px",
        //     style: {
        //         backgroundColor: '#dceefb'
        //     }
        // },
        {
            name: (
                <>
                    Net Paid (Incl. Tax)<br />In CAD
                </>
            ),
            selector: row => row.paid_currency === 'CAD'
                ? `$ ${formatAmount(netPaymentWithTax(row.total_amount_cad, row.tax_charge_cad, row.refund_amount_cad))}`
                : '-',
            sortable: true,
            width: "130px",
            style: {
                backgroundColor: '#ffffe0'
            }
        },
        {
            name: 'Program Type',
            selector: row => row.activity_type,
            sortable: true,
            width: "130px"
        },
        {
            name: 'Platform Type',
            selector: row => row.platform_type,
            sortable: true,
            width: "130px"
        },
        {
            name: 'Duration',
            selector: row => row.duration,
            sortable: true,
            width: "160px"
        },
        {
            name: 'Coupon Number',
            selector: row => row.coupan_number ? row.coupan_number : "-",
            sortable: true,
            width: "155px"
        },
        {
            name: 'Refund Status',
            selector: row => (
                <button className="bg-success border-0 text-light px-1 fw-500" style={{ cursor: "default" }}>
                    {row.payment_status}
                </button>
            ),
            sortable: true,
            width: "130px"
        },
        // NEW: "Class Date" — the date of the CLASS / MEETING this row is
        // about, which is not the same thing as the payment date beside it: a
        // subscription paid for in January can be a session held in March.
        // Here it sits beside the refund date, so a refund can be read against the
        // session it was given back for.
        // The API supplies it as `class_date`, already dd/mm/YYYY. A row with no
        // meeting behind it (offline course, quiz) has no class date and shows
        // "-" — that is the data, not a lookup failure.
        {
            name: 'Class Date',
            // A Multi-Day row prints its span ("first - last") rather than one
            // date; see classDateText. Old single-date selector:
            // selector: row => row.class_date ? row.class_date : '-',
            // cellExport: row => row.class_date ? row.class_date : '-',
            selector: row => classDateText(row),
            cellExport: row => classDateText(row),
            sortable: true,
            // A Multi-Day row prints a RANGE here, which does not fit the width a
            // single date needed — react-data-table-component clipped it to
            // "17/08/2026 - _". Widened, and wrap:true so it breaks onto a second
            // line rather than being cut off if it still does not fit.
            wrap: true,
            // width: "130px"
            // width: "150px"
            width: "190px"
        },
        // NEW: "Total Meetings" — how many meetings this row is for. An ongoing
        // row is billed per meeting and counts 1; a One-Time / Multi-Day purchase
        // counts every meeting it bought. Rows with none (offline course, quiz,
        // referral credit) show "-". See totalMeetingsText.
        {
            name: 'Total Meetings',
            selector: row => totalMeetingsText(row),
            cellExport: row => totalMeetingsText(row),
            sortable: true,
            width: "130px"
        },
        {
            name: 'Transaction Id',
            selector: row => row.refund_id,
            sortable: true,
            width: "260px"
        },
    ]

    const paymentReceivedColumns = [
        {
            name: 'No',
            selector: row => row.id,
            sortable: false,
            width: "60px"
        },
        {
            name: (
                <>
                    Payment<br />Date
                </>
            ),
            // selector: row => {
            //     const date = new Date(row.enrolled_at);
            //     const day = date.getDate();
            //     const month = date.getMonth() + 1;
            //     const formattedMonth = month < 10 ? `0${month}` : month;
            //     const formattedDay = day < 10 ? `0${day}` : day;
            //     const year = date.getFullYear();
            //     return `${formattedDay}-${formattedMonth}-${year}`;
            // },
            selector: row => row.enrolled_at,
            sortable: true,
            width: "100px"
        },
        // NEW: "Class Date" — the date of the CLASS / MEETING this row is
        // about, which is not the same thing as the payment date beside it: a
        // subscription paid for in January can be a session held in March.
        // An ongoing-class row is one meeting, so it shows that meeting's date.
        // The API supplies it as `class_date`, already dd/mm/YYYY. A row with no
        // meeting behind it (offline course, quiz) has no class date and shows
        // "-" — that is the data, not a lookup failure.
        {
            name: 'Class Date',
            // A Multi-Day row prints its span ("first - last") rather than one
            // date; see classDateText. Old single-date selector:
            // selector: row => row.class_date ? row.class_date : '-',
            // cellExport: row => row.class_date ? row.class_date : '-',
            selector: row => classDateText(row),
            cellExport: row => classDateText(row),
            sortable: true,
            // A Multi-Day row prints a RANGE here, which does not fit the width a
            // single date needed — react-data-table-component clipped it to
            // "17/08/2026 - _". Widened, and wrap:true so it breaks onto a second
            // line rather than being cut off if it still does not fit.
            wrap: true,
            // width: "130px"
            // width: "150px"
            width: "190px"
        },
        // NEW: "Total Meetings" — how many meetings this row is for. An ongoing
        // row is billed per meeting and counts 1; a One-Time / Multi-Day purchase
        // counts every meeting it bought. Rows with none (offline course, quiz,
        // referral credit) show "-". See totalMeetingsText.
        {
            name: 'Total Meetings',
            selector: row => totalMeetingsText(row),
            cellExport: row => totalMeetingsText(row),
            sortable: true,
            width: "130px"
        },
        {
            name: 'From Code',
            selector: row => row.usercode ? row.usercode : "-",
            sortable: true,
            width: "120px"
        },
        {
            name: 'From Name',
            selector: row => row.from_account,
            sortable: true,
            width: "120px",
            cell: row => (
                <span
                    onClick={() => navigate('/user_details', { state: { id: row.user_id } })}
                    style={{ color: "rgb(87, 8, 97)", border: "none", background: "none" }}
                >
                    {row.from_account}
                </span>
            )
        },
        {
            name: 'To Code',
            selector: row => row.teachercode ? row.teachercode : "-",
            sortable: true,
            width: "100px"
        },
        {
            name: 'To Name',
            selector: row => row.to_account,
            sortable: true,
            width: "110px",
            cell: row => (
                row.teacher_category == 'teacher' ? (
                    <span
                        onClick={() => navigate('/teacher_details', { state: { teacherId: row.teacher_id } })}
                        style={{ color: "rgb(87, 8, 97)", border: "none", background: "none", cursor: 'pointer' }}
                    >
                        {row.to_account}
                    </span>
                ) : row.teacher_category == 'organization' ? (
                    <span
                        onClick={() => navigate('/org_details', { state: { userId: row.teacher_id } })}
                        style={{ color: "rgb(87, 8, 97)", border: "none", background: "none", cursor: 'pointer' }}
                    >
                        {row.to_account}
                    </span>
                ) : (
                    <span>{row.to_account}</span>
                )
            )
        },
        {
            name: (
                <>
                    Learner <br /> Name
                </>
            ),
            selector: row => row.learner_name,
            cellExport: row => row.learner_name,
            width: '130px'
        },
        {
            name: (
                <>
                    Learner <br /> Age
                </>
            ),
            selector: row => row.learner_category === 'above_18' ? 'Adult' : (row.learner_age ? row.learner_age : '-'),
            cellExport: row => row.learner_category === 'above_18' ? 'Adult' : (row.learner_age ? row.learner_age : '-'),
            width: '100px'
        },
        {
            name: (
                <>
                    Learner <br /> Country
                </>
            ),
            selector: row => row.learner_country,
            cellExport: row => row.learner_country,
            width: '130px'
        },
        {
            name: (
                <>
                    Learner <br /> Province
                </>
            ),
            selector: row => row.learner_state ? row.learner_state : '-',
            cellExport: row => row.learner_state ? row.learner_state : '-',
            width: '130px',
        },
        {
            name: (
                <>
                    Teacher <br /> Country
                </>
            ),
            selector: row => row.teacher_country,
            cellExport: row => row.teacher_country,
            width: '130px'
        },
        {
            name: (
                <>
                    Teacher <br /> Province
                </>
            ),
            selector: row => row.teacher_state ? row.teacher_state : '-',
            cellExport: row => row.teacher_state ? row.teacher_state : '-',
            width: '130px',
        },
        {
            name: 'Program Name',
            selector: row => row.activity_title,
            sortable: true,
            width: "160px",
            cell: row => (
                <span
                    style={{ color: "rgb(87, 8, 97)", border: "none", background: "none", cursor: 'pointer' }}
                    onClick={() => navigate('/ewallet_group_transactions', {
                        state: {
                            learner_id: row.user_id,
                            activity_id: row.activity_id
                        }
                    })}>{row.activity_title}</span>
            )
        },
        {
            name: (
                <>
                    Program<br />Type
                </>
            ),
            selector: row => row.activity_type,
            sortable: true,
            width: "100px"
        },
        {
            name: (
                <>
                    Platform<br />Type
                </>
            ),
            selector: row => row.platform_type ? row.platform_type : "Online",
            sortable: true,
            width: "100px"
        },
        {
            name: 'Coupon Number',
            selector: row => row.coupan_number ? row.coupan_number : "-",
            sortable: true,
            width: "155px"
        },
        {
            name: (
                <>
                    Class Price<br />In USD
                </>
            ),
            selector: row => row.paid_currency === 'CAD' ? '-' : `$ ${formatAmount(row.class_price_usd || row.class_price || 0)}`,
            sortable: true,
            width: "100px",
            style: {
                backgroundColor: '#dceefb'
            }
        },
        // "Class Price Before Tax In USD" - commented out alongside the "Tax Price In
        // USD" column below it: with no USD tax to take off, it printed the very same
        // figure as the column after it, which now carries the tax-free title
        // "Total Class Price In USD".
        //
        // Kept verbatim so the column can be restored by deleting this comment.
        // {
        //     name: (
        //         <>
        //             Class Price Before Tax<br />In USD
        //         </>
        //     ),
        //     selector: row => row.paid_currency === 'CAD' ? '-' : `$ ${formatAmount(toAmount(row.total_amount_usd) - toAmount(row.tax_charge_usd))}`,
        //     sortable: true,
        //     width: "100px",
        //     style: {
        //         backgroundColor: '#dceefb'
        //     }
        // },
        // "Tax Price In USD" - commented out: no tax is charged on a US sale, so the
        // column only ever printed $0.00. GST/HST is charged on the BUYER's province
        // (common.gst_hst_details reads the buyer's own state_id) and a buyer in a
        // Canadian province is billed in CAD, so every taxed row lands in the CAD
        // block instead, where "Tax Price In CAD" still reports it. Same treatment,
        // same reason, as the Earnings page's USD tax column.
        //
        // Kept verbatim so the column can be restored by deleting this comment.
        // {
        //     name: (
        //         <>
        //             Tax Price<br />In USD
        //         </>
        //     ),
        //     selector: row => row.paid_currency === 'CAD' ? '-' : `$ ${formatAmount(row.tax_charge_usd)}`,
        //     sortable: true,
        //     width: "80px",
        //     style: {
        //         backgroundColor: '#dceefb'
        //     }
        // },
        // NEW column: the tax still held on the row — the "Tax Price" beside it less
        // the tax refunded. Accent-coloured, and the column the "Total Tax Collected
        // after deductions" card totals. See netTaxRetained.
        // {
        //     id: TOTAL_TAX_COLUMN_ID_USD,
        //     name: (
        //         <>
        //             Total Tax<br />In USD
        //         </>
        //     ),
        //     selector: row => row.paid_currency === 'CAD'
        //         ? '-'
        //         : `$ ${formatAmount(netTaxRetained(row.total_amount_usd, row.tax_charge_usd, row.refund_amount_usd))}`,
        //     sortable: true,
        //     width: "80px",
        //     style: taxCellStyle
        // },
        // NEW column: "Net Paid (Excl. Tax)" + "Total Tax" (see
        // netPaymentWithTax). USD counterpart, commented out like the USD tax
        // column above it.
        // {
        //     name: (
        //         <>
        //             Net Paid (Incl. Tax)<br />In USD
        //         </>
        //     ),
        //     selector: row => row.paid_currency === 'CAD'
        //         ? '-'
        //         : `$ ${formatAmount(netPaymentWithTax(row.total_amount_usd, row.tax_charge_usd, row.refund_amount_usd))}`,
        //     sortable: true,
        //     width: "130px",
        //     style: {
        //         backgroundColor: '#dceefb'
        //     }
        // },
        {
            name: (
                <>
                    Total Class Price<br />In USD
                </>
            ),
            // Titled without the tax wording: with no USD tax there is no "after
            // tax" for a USD row to be after, and the pre-tax column that used to
            // sit above this one is commented out for the same reason. The cell is
            // unchanged - only the heading is. ("In CAD" keeps its Before/After
            // Tax titles, where the tax is real.)
            selector: row => row.paid_currency === 'CAD' ? '-' : `$ ${formatAmount(row.total_amount_usd)}`,
            sortable: true,
            width: "80px",
            style: {
                backgroundColor: '#dceefb'
            }
        },
        // NEW column (added alongside the existing ones — nothing above/below changed)
        // Accent-coloured: this is the column the "Total Received" card totals.
        {
            id: NET_PAID_COLUMN_ID_USD,
            name: (
                <>
                    Net Paid<br />In USD
                </>
            ),
            // Titled without "(Excl. Tax)" - no USD tax to be exclusive of. See
            // the "Total Class Price In USD" column above.
            selector: row => row.paid_currency === 'CAD'
                ? '-'
                : `$ ${formatAmount(netAmountPaidByLearner(row.total_amount_usd, row.tax_charge_usd, row.refund_amount_usd))}`,
            sortable: true,
            width: "120px",
            style: netPaidCellStyle
        },
        {
            name: (
                <>
                    Teacher Earning<br />In USD
                </>
            ),
            // Now derived from the new "Net Paid (Excl. Tax)" column (refunds
            // deducted) instead of the gross amount — see netEarning above.
            // Previous gross-amount version, kept for reference:
            // selector: row => row.paid_currency === 'CAD' ? '-' : `$ ${formatAmount(row.teacher_earning_usd)}`,
            selector: row => row.paid_currency === 'CAD'
                ? '-'
                : `$ ${formatAmount(netEarning(row.teacher_earning_usd, row.total_amount_usd, row.tax_charge_usd, row.refund_amount_usd))}`,
            sortable: true,
            width: "80px",
            style: {
                backgroundColor: '#dceefb'
            }
        },
        // Plain (no accent) since the card stopped being this column's total —
        // it is now the total of "Admin Earnings After Deducting Fees", two columns along.
        {
            id: ADMIN_EARNING_COLUMN_ID_USD,
            name: (
                <>
                    Admin Earning<br />In USD
                </>
            ),
            // Now derived from the new "Net Paid (Excl. Tax)" column (refunds
            // deducted) instead of the gross amount — see netEarning above.
            // Previous gross-amount version, kept for reference:
            // selector: row => row.paid_currency === 'CAD' ? '-' : `$ ${formatAmount(row.admin_earning_usd)}`,
            selector: row => row.paid_currency === 'CAD'
                ? '-'
                : `$ ${formatAmount(netEarning(row.admin_earning_usd, row.total_amount_usd, row.tax_charge_usd, row.refund_amount_usd))}`,
            sortable: true,
            width: "80px",
            // No accent tint any more — the revenue accent moved to the
            // "Admin Earnings After Deducting Fees" column, which is what the "Total
            // Revenue" card is the total of. Previous version:
            //     style: adminEarningCellStyle
        },
        // NEW column: the ACTUAL Stripe processing fee on this transaction — see
        // feeCell. Placed right after "Admin Earning" because
        // the "Total Revenue" card is that column's total LESS this one's.
        {
            id: FEE_COLUMN_ID_USD,
            name: (
                <>
                    Fees<br />In USD
                </>
            ),
            selector: row => feeCell(row, 'USD'),
            sortable: true,
            width: "80px"
        },
        // NEW column: the two columns before it subtracted — Admin Earning less
        // the actual Stripe fee. Accent-coloured: this is the column the "Total
        // Revenue" card totals. See adminEarningAfterFee.
        {
            id: ADMIN_EARNING_AFTER_FEE_COLUMN_ID_USD,
            name: (
                <>
                    Admin Earnings After Deducting Fees<br />In USD
                </>
            ),
            selector: row => adminEarningAfterFeeCell(row, 'USD', row.total_amount_usd),
            sortable: true,
            width: "110px",
            style: adminEarningAfterFeeCellStyle
        },
        {
            name: (
                <>
                    Class Price<br />In CAD
                </>
            ),
            selector: row => row.paid_currency === 'CAD' ? `$ ${formatAmount(row.class_price_cad || 0)}` : '-',
            sortable: true,
            width: "100px",
            style: {
                backgroundColor: '#ffffe0'
            }
        },
        {
            name: (
                <>
                    Class Price Before Tax<br />In CAD
                </>
            ),
            selector: row => row.paid_currency === 'CAD' ? `$ ${formatAmount(toAmount(row.total_amount_cad) - toAmount(row.tax_charge_cad))}` : '-',
            sortable: true,
            width: "100px",
            style: {
                backgroundColor: '#ffffe0'
            }
        },
        {
            id: TOTAL_TAX_COLUMN_ID_CAD,
            name: (
                <>
                    Total Tax<br />In CAD
                </>
            ),
            selector: row => row.paid_currency === 'CAD'
                ? `$ ${formatAmount(netTaxRetained(row.total_amount_cad, row.tax_charge_cad, row.refund_amount_cad))}`
                : '-',
            sortable: true,
            width: "80px",
            style: taxCellStyle
        },
        // NEW column: "Net Paid (Excl. Tax)" + "Total Tax" — the whole
        // tax-inclusive amount left with the platform for the row. See
        // netPaymentWithTax. No accent colour: no balance card totals it.
        {
            name: (
                <>
                    Net Paid (Incl. Tax)<br />In CAD
                </>
            ),
            selector: row => row.paid_currency === 'CAD'
                ? `$ ${formatAmount(netPaymentWithTax(row.total_amount_cad, row.tax_charge_cad, row.refund_amount_cad))}`
                : '-',
            sortable: true,
            width: "130px",
            style: {
                backgroundColor: '#ffffe0'
            }
        },
        {
            name: (
                <>
                    Tax Price<br />In CAD
                </>
            ),
            selector: row => row.paid_currency === 'CAD' ? `$ ${formatAmount(row.tax_charge_cad)}` : '-',
            sortable: true,
            width: "80px",
            style: {
                backgroundColor: '#ffffe0'
            }
        },
        // NEW column: the tax still held on the row — the "Tax Price" beside it less
        // the tax refunded. Accent-coloured, and the column the "Total Tax Collected
        // after deductions" card totals. See netTaxRetained.

        {
            name: (
                <>
                    Class Price After Tax<br />In CAD
                </>
            ),
            selector: row => row.paid_currency === 'CAD' ? `$ ${formatAmount(row.total_amount_cad)}` : '-',
            sortable: true,
            width: "80px",
            style: {
                backgroundColor: '#ffffe0'
            }
        },
        // NEW column (added alongside the existing ones — nothing above/below changed)
        // Accent-coloured: this is the column the "Total Received" card totals.
        {
            id: NET_PAID_COLUMN_ID_CAD,
            name: (
                <>
                    Net Paid (Excl. Tax)<br />In CAD
                </>
            ),
            selector: row => row.paid_currency === 'CAD'
                ? `$ ${formatAmount(netAmountPaidByLearner(row.total_amount_cad, row.tax_charge_cad, row.refund_amount_cad))}`
                : '-',
            sortable: true,
            width: "120px",
            style: netPaidCellStyle
        },
        {
            name: (
                <>
                    Teacher Earning<br />In CAD
                </>
            ),
            // Now derived from the new "Net Paid (Excl. Tax)" column (refunds
            // deducted) instead of the gross amount — see netEarning above.
            // Previous gross-amount version, kept for reference:
            // selector: row => row.paid_currency === 'CAD' ? `$ ${formatAmount(row.teacher_earning_cad)}` : '-',
            selector: row => row.paid_currency === 'CAD'
                ? `$ ${formatAmount(netEarning(row.teacher_earning_cad, row.total_amount_cad, row.tax_charge_cad, row.refund_amount_cad))}`
                : '-',
            sortable: true,
            width: "80px",
            style: {
                backgroundColor: '#ffffe0'
            }
        },
        // Plain (no accent) since the card stopped being this column's total —
        // it is now the total of "Admin Earnings After Deducting Fees", two columns along.
        {
            id: ADMIN_EARNING_COLUMN_ID_CAD,
            name: (
                <>
                    Admin Earning<br />In CAD
                </>
            ),
            // Now derived from the new "Net Paid (Excl. Tax)" column (refunds
            // deducted) instead of the gross amount — see netEarning above.
            // Previous gross-amount version, kept for reference:
            // selector: row => row.paid_currency === 'CAD' ? `$ ${formatAmount(row.admin_earning_cad)}` : '-',
            selector: row => row.paid_currency === 'CAD'
                ? `$ ${formatAmount(netEarning(row.admin_earning_cad, row.total_amount_cad, row.tax_charge_cad, row.refund_amount_cad))}`
                : '-',
            sortable: true,
            width: "80px",
            // No accent tint any more — the revenue accent moved to the
            // "Admin Earnings After Deducting Fees" column, which is what the "Total
            // Revenue" card is the total of. Previous version:
            //     style: adminEarningCellStyle
        },
        // NEW column: the CAD side of the same actual Stripe fee — see feeCell.
        {
            id: FEE_COLUMN_ID_CAD,
            name: (
                <>
                    Fees<br />In CAD
                </>
            ),
            selector: row => feeCell(row, 'CAD'),
            sortable: true,
            width: "80px"
        },
        // NEW column: the CAD side of the same subtraction — see the USD column
        // above and adminEarningAfterFee.
        {
            id: ADMIN_EARNING_AFTER_FEE_COLUMN_ID_CAD,
            name: (
                <>
                    Admin Earnings After Deducting Fees<br />In CAD
                </>
            ),
            selector: row => adminEarningAfterFeeCell(row, 'CAD', row.total_amount_cad),
            sortable: true,
            width: "110px",
            style: adminEarningAfterFeeCellStyle
        },
        {
            name: 'Duration',
            selector: row => row.duration,
            sortable: true,
            width: "155px"
        },
        {
            name: (
                <>
                    Payment<br />Status
                </>
            ),
            selector: row => (
                <button className="bg-success border-0 text-light px-1 fw-500" style={{ cursor: "default" }}> {row.payment_status}</button>
            ),
            sortable: true,
            width: "140px"
        },
        {
            name: 'Transaction Id',
            selector: row => row.transaction_id,
            sortable: true,
            width: "260px"
        },
        // {
        //     name: 'Action',
        //     width: "80px",
        //     selector: row => row.id,
        //     center: true,
        //     cell: (row) => <>
        //         <button type="button" className="btn" style={{ boxShadow: 'none' }} id={row.id} ><a className="material-icons"
        //             onClick={() => {
        //                 const idToSend = (row.learner_id == null || row.learner_id === "") ? row.user_id : row.learner_id;
        //                 const payload = {
        //                     activity_id: row.activity_id.toString(),
        //                     activity_type: row.activity_type,
        //                     ...(row.learner_id ? { learnerId: row.learner_id } : { userId: row.user_id })
        //                 };
        //                 downloadPdf(payload);
        //             }}

        //             id={row.id} >  <svg width={30} height={30} viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
        //                 <path d="M15 0C12.0333 0 9.13319 0.879735 6.66645 2.52796C4.19972 4.17618 2.27713 6.51886 1.14181 9.25975C0.00649929 12.0006 -0.290551 15.0166 0.288228 17.9264C0.867006 20.8361 2.29562 23.5088 4.39341 25.6066C6.49119 27.7044 9.16394 29.133 12.0737 29.7118C14.9834 30.2906 17.9994 29.9935 20.7403 28.8582C23.4812 27.7229 25.8238 25.8003 27.4721 23.3336C29.1203 20.8668 30 17.9667 30 15C30 11.0218 28.4197 7.20644 25.6066 4.3934C22.7936 1.58035 18.9783 0 15 0ZM19.9206 24.6625H10.0794C9.74786 24.6625 9.42992 24.5308 9.1955 24.2964C8.96108 24.062 8.82938 23.744 8.82938 23.4125C8.82938 23.081 8.96108 22.763 9.1955 22.5286C9.42992 22.2942 9.74786 22.1625 10.0794 22.1625H19.9206C20.2522 22.1625 20.5701 22.2942 20.8045 22.5286C21.0389 22.763 21.1706 23.081 21.1706 23.4125C21.1706 23.744 21.0389 24.062 20.8045 24.2964C20.5701 24.5308 20.2522 24.6625 19.9206 24.6625ZM20.8044 15.26L15.88 20.1875C15.6456 20.4218 15.3277 20.5535 14.9963 20.5535C14.6648 20.5535 14.3469 20.4218 14.1125 20.1875L9.19563 15.2713C8.96075 15.0369 8.8286 14.7188 8.82824 14.387C8.82789 14.0551 8.95937 13.7368 9.19376 13.5019C9.42814 13.267 9.74623 13.1348 10.0781 13.1345C10.4099 13.1341 10.7283 13.2656 10.9631 13.5L13.75 16.2869V5.22813C13.75 4.8966 13.8817 4.57866 14.1161 4.34424C14.3505 4.10982 14.6685 3.97813 15 3.97813C15.3315 3.97813 15.6495 4.10982 15.8839 4.34424C16.1183 4.57866 16.25 4.8966 16.25 5.22813V16.2794L19.0369 13.4925C19.2726 13.2648 19.5884 13.1388 19.9161 13.1417C20.2439 13.1445 20.5574 13.276 20.7892 13.5077C21.0209 13.7395 21.1524 14.053 21.1552 14.3807C21.1581 14.7085 21.0321 15.0242 20.8044 15.26Z" fill="#570861" />
        //             </svg></a> </button></>
        // }
    ]

    const AllDataColumns = [
        {
            name: 'No',
            selector: row => row.id,
            sortable: false,
            width: "50px"
        },
        {
            // name: 'Date',
            name: 'Purchase Date',
            selector: row => {
                // let date;
                // if (row.payment_status === "paid") {
                //     date = new Date(row.created_at);
                // } else if (row.payment_status === "succeeded") {
                //     date = new Date(row.enrolled_at);
                // } else if (row.payment_status === "refunded") {
                //     date = new Date(row.enrolled_at);
                // } else {
                //     date = new Date();
                // }

                // const day = date.getDate();
                // const month = date.getMonth() + 1;
                // const formattedMonth = month < 10 ? `0${month}` : month;
                // const formattedDay = day < 10 ? `0${day}` : day;
                // const year = date.getFullYear();
                // return `${formattedDay}-${formattedMonth}-${year}`;
            },
            sortable: true,
            // width: "100px",
            width: "130px",
            selector: row => row.enrolled_at,
        },
        // NEW: "Class Date" — the date of the CLASS / MEETING this row is
        // about, which is not the same thing as the payment date beside it: a
        // subscription paid for in January can be a session held in March.
        // This tab mixes payments, refunds and payouts; every one of those row
        // shapes carries the field, so the column reads the same down the table.
        // The API supplies it as `class_date`, already dd/mm/YYYY. A row with no
        // meeting behind it (offline course, quiz) has no class date and shows
        // "-" — that is the data, not a lookup failure.
        {
            name: 'Class Date',
            // A Multi-Day row prints its span ("first - last") rather than one
            // date; see classDateText. Old single-date selector:
            // selector: row => row.class_date ? row.class_date : '-',
            // cellExport: row => row.class_date ? row.class_date : '-',
            selector: row => classDateText(row),
            cellExport: row => classDateText(row),
            sortable: true,
            // A Multi-Day row prints a RANGE here, which does not fit the width a
            // single date needed — react-data-table-component clipped it to
            // "17/08/2026 - _". Widened, and wrap:true so it breaks onto a second
            // line rather than being cut off if it still does not fit.
            wrap: true,
            // width: "130px"
            // width: "150px"
            width: "190px"
        },
        // NEW: "Total Meetings" — how many meetings this row is for. An ongoing
        // row is billed per meeting and counts 1; a One-Time / Multi-Day purchase
        // counts every meeting it bought. Rows with none (offline course, quiz,
        // referral credit) show "-". See totalMeetingsText.
        {
            name: 'Total Meetings',
            selector: row => totalMeetingsText(row),
            cellExport: row => totalMeetingsText(row),
            sortable: true,
            width: "130px"
        },
        {
            name: (
                <>
                    From<br />Code
                </>
            ),
            selector: row => {
                if (row.payment_status === "refunded") {
                    return row.teachercode ? row.teachercode : "-";
                } else if (row.payment_status === "succeeded") {
                    return row.usercode ? row.usercode : "-";
                } else if (row.payment_status === "paid") {
                    return "-";
                } else {
                    return row.usercode ? row.usercode : "-";
                }
            },
            sortable: true,
            width: "90px"
        },
        {
            name: 'From Name',
            selector: row => {
                if (row.payment_status === "refunded") {
                    return row.to_account;
                } else if (row.payment_status === "succeeded") {
                    return row.from_account;
                } else if (row.payment_status === "paid") {
                    return "StudentScholar";
                } else {
                    return row.from_account;
                }
            },
            sortable: true,
            width: "120px",
            cell: row => {
                let displayName;

                if (row.payment_status === "refunded") {
                    displayName = row.to_account;
                } else if (row.payment_status === "succeeded") {
                    displayName = row.from_account;
                } else if (row.payment_status === "paid") {
                    displayName = "StudentScholar";
                } else {
                    displayName = row.from_account;
                }

                if (row.payment_status === "succeeded") {
                    return (
                        <span
                            onClick={() => navigate('/user_details', { state: { id: row.user_id } })}
                            style={{ color: "rgb(87, 8, 97)", border: "none", background: "none", cursor: "pointer" }}
                        >
                            {displayName}
                        </span>
                    );
                } else if (row.payment_status === "refunded") {
                    if (row.teacher_category == 'teacher') {
                        return (
                            <span
                                onClick={() => navigate('/teacher_details', { state: { teacherId: row.teacher_id } })}
                                style={{ color: "rgb(87, 8, 97)", border: "none", background: "none", cursor: 'pointer' }}
                            >
                                {displayName}
                            </span>
                        )
                    } else if (row.teacher_category == 'organization') {
                        return (
                            <span
                                onClick={() => navigate('/org_details', { state: { userId: row.teacher_id } })}
                                style={{ color: "rgb(87, 8, 97)", border: "none", background: "none", cursor: 'pointer' }}
                            >
                                {displayName}
                            </span>
                        )
                    } else {
                        return (
                            <span
                                style={{ color: "rgb(87, 8, 97)", border: "none", background: "none" }}
                            >
                                {displayName}
                            </span>
                        );
                    }

                } else {
                    return (
                        <span
                            style={{ color: "rgb(87, 8, 97)", border: "none", background: "none" }}
                        >
                            {displayName}
                        </span>
                    );
                }
            }
        },
        {
            name: (
                <>
                    To<br />Code
                </>
            ),
            selector: row => {
                if (row.payment_status === "refunded") {
                    return row.usercode ? row.usercode : "-";
                } else if (row.payment_status === "succeeded") {
                    return row.teachercode ? row.teachercode : "-";
                } else if (row.payment_status === "paid") {
                    return row.teachercode ? row.teachercode : "-";
                } else {
                    return row.teachercode ? row.teachercode : "-";
                }
            },
            sortable: true,
            width: "90px"
        },
        {
            name: 'To Name',
            selector: row => {
                if (row.payment_status === "refunded") {
                    return row.from_account;
                } else if (row.payment_status === "succeeded") {
                    return row.to_account;
                } else if (row.payment_status === "paid" || row.source == "payment_paid_to_teachers") {
                    return row.teacher_name;
                } else {
                    return row.to_account;
                }
            },
            sortable: true,
            width: "110px",
            cell: row => {
                let displayName;

                if (row.payment_status === "refunded") {
                    displayName = row.from_account;
                } else if (row.payment_status === "succeeded") {
                    displayName = row.to_account;
                } else if (row.payment_status == "paid" || row.source == "payment_paid_to_teachers") {
                    displayName = row.teacher_name;
                } else {
                    displayName = row.to_account;
                }

                if (row.payment_status === "refunded") {
                    return (
                        <span
                            onClick={() => navigate('/user_details', { state: { id: row.user_id } })}
                            style={{ color: "rgb(87, 8, 97)", border: "none", background: "none", cursor: "pointer" }}
                        >
                            {displayName}
                        </span>
                    );
                } else if (row.payment_status === "succeeded") {
                    if (row.teacher_category == 'teacher') {
                        return (
                            <span
                                onClick={() => navigate('/teacher_details', { state: { teacherId: row.teacher_id } })}
                                style={{ color: "rgb(87, 8, 97)", border: "none", background: "none", cursor: 'pointer' }}
                            >
                                {displayName}
                            </span>
                        )
                    } else if (row.teacher_category == 'organization') {
                        return (
                            <span
                                onClick={() => navigate('/org_details', { state: { userId: row.teacher_id } })}
                                style={{ color: "rgb(87, 8, 97)", border: "none", background: "none", cursor: 'pointer' }}
                            >
                                {displayName}
                            </span>
                        )
                    } else {
                        return (
                            <span
                                style={{ color: "rgb(87, 8, 97)", border: "none", background: "none" }}
                            >
                                {displayName}
                            </span>
                        );
                    }
                } else if (row.payment_status === "paid") {

                    if (row.teacher_category == 'teacher') {
                        return (
                            <span
                                onClick={() => navigate('/teacher_details', { state: { teacherId: row.teacher_id } })}
                                style={{ color: "rgb(87, 8, 97)", border: "none", background: "none", cursor: 'pointer' }}
                            >
                                {displayName}
                            </span>
                        )
                    } else if (row.teacher_category == 'organization') {
                        return (
                            <span
                                onClick={() => navigate('/org_details', { state: { userId: row.teacher_id } })}
                                style={{ color: "rgb(87, 8, 97)", border: "none", background: "none", cursor: 'pointer' }}
                            >
                                {displayName}
                            </span>
                        )
                    } else {
                        return (
                            <span
                                style={{ color: "rgb(87, 8, 97)", border: "none", background: "none" }}
                            >
                                {displayName}
                            </span>
                        );
                    }
                } else {
                    return (
                        <span
                            style={{ color: "rgb(87, 8, 97)", border: "none", background: "none" }}
                        >
                            {displayName}
                        </span>
                    );
                }
            }
        },
        {
            name: (
                <>
                    Learner <br /> Name
                </>
            ),
            selector: row => row.learner_name,
            cellExport: row => row.learner_name,
            width: '130px'
        },
        {
            name: (
                <>
                    Learner <br /> Age
                </>
            ),
            selector: row => row.learner_category === 'above_18' ? 'Adult' : (row.learner_age ? row.learner_age : '-'),
            cellExport: row => row.learner_category === 'above_18' ? 'Adult' : (row.learner_age ? row.learner_age : '-'),
            width: '100px'
        },
        {
            name: (
                <>
                    Learner <br /> Country
                </>
            ),
            selector: row => row.learner_country,
            cellExport: row => row.learner_country,
            width: '130px'
        },
        {
            name: (
                <>
                    Learner <br /> Province
                </>
            ),
            selector: row => row.learner_state ? row.learner_state : '-',
            cellExport: row => row.learner_state ? row.learner_state : '-',
            width: '130px',
        },
        {
            name: (
                <>
                    Teacher <br /> Country
                </>
            ),
            selector: row => row.teacher_country,
            cellExport: row => row.teacher_country,
            width: '130px'
        },
        {
            name: (
                <>
                    Teacher <br /> Province
                </>
            ),
            selector: row => row.teacher_state ? row.teacher_state : '-',
            cellExport: row => row.teacher_state ? row.teacher_state : '-',
            width: '130px',
        },
        {
            name: 'Program Name',
            selector: row => row.activity_title ? row.activity_title : '-',
            sortable: true,
            width: "180px",
            cell: row => (
                <span
                    style={{ color: "rgb(87, 8, 97)", border: "none", background: "none", cursor: 'pointer' }}
                    onClick={() => navigate('/ewallet_group_transactions', {
                        state: {
                            learner_id: row.user_id,
                            activity_id: row.activity_id
                        }
                    })}>{row.activity_title}</span>
            )
        },
        {
            name: 'Program Type',
            selector: row => row.activity_type ? row.activity_type : "-",
            sortable: true,
            width: "130px"
        },
        {
            name: 'Platform Type',
            selector: row => row.platform_type ? row.platform_type : "Online",
            sortable: true,
            width: "130px"
        },
        {
            name: (
                <>
                    Class<br />Type
                </>
            ),
            selector: row => row.class_type ? row.class_type : "-",
            sortable: true,
            width: "120px"
        },
        {
            name: 'Duration',
            selector: row => row.duration ? row.duration : "-",
            sortable: true,
            width: "155px"
        },
        {
            name: (
                <>
                    Total<br />Classes
                </>
            ),
            selector: row => row.total_classes ? row.total_classes : "-",
            sortable: true,
            width: "100px"
        },
        {
            name: 'Coupon Number',
            selector: row => row.coupan_number ? row.coupan_number : "-",
            sortable: true,
            width: "155px"
        },
        {
            name: (
                <>
                    Class Price<br /> In USD
                </>
            ),
            selector: row => row.paid_currency === 'CAD' ? '-' : `$ ${formatAmount(row.class_price_usd || row.class_price || 0)}`,
            sortable: true,
            width: "100px",
            style: {
                backgroundColor: '#dceefb'
            }
        },
        // "Class Price Before Tax In USD" - commented out alongside the "Tax Price In
        // USD" column below it: with no USD tax to take off, it printed the very same
        // figure as the column after it, which now carries the tax-free title
        // "Total Class Price In USD".
        //
        // Kept verbatim so the column can be restored by deleting this comment.
        // {
        //     name: (
        //         <>
        //             Class Price Before Tax<br /> In USD
        //         </>
        //     ),
        //     selector: row => row.paid_currency === 'CAD' ? '-' : `$ ${formatAmount((row.payment_status === "paid" ? toAmount(row.teacher_amount_usd) : toAmount(row.total_amount_usd)) - toAmount(row.tax_charge_usd))}`,
        //     sortable: true,
        //     width: "125px",
        //     style: {
        //         backgroundColor: '#dceefb'
        //     }
        // },
        // "Tax Price In USD" - commented out: no tax is charged on a US sale, so the
        // column only ever printed $0.00. GST/HST is charged on the BUYER's province
        // (common.gst_hst_details reads the buyer's own state_id) and a buyer in a
        // Canadian province is billed in CAD, so every taxed row lands in the CAD
        // block instead, where "Tax Price In CAD" still reports it. Same treatment,
        // same reason, as the Earnings page's USD tax column.
        //
        // Kept verbatim so the column can be restored by deleting this comment.
        // {
        //     name: (
        //         <>
        //             Tax Price<br /> In USD
        //         </>
        //     ),
        //     selector: row => row.paid_currency === 'CAD' ? '-' : `$ ${formatAmount(row.tax_charge_usd)}`,
        //     sortable: true,
        //     width: "125px",
        //     style: {
        //         backgroundColor: '#dceefb'
        //     }
        // },
        {
            name: (
                <>
                    Total Class Price<br /> In USD
                </>
            ),
            // Titled without the tax wording - see the Payments Received tab. The
            // pre-tax column that used to sit above this one is commented out with
            // the USD tax column, both being the same figure without a USD tax.
            selector: row => row.paid_currency === 'CAD' ? '-' : `$ ${formatAmount(row.payment_status === "paid" ? row.teacher_amount_usd : row.total_amount_usd)}`,
            sortable: true,
            width: "155px",
            style: {
                backgroundColor: '#dceefb'
            }
        },
        // Accent-coloured: this is the column the "Total Refund Provided" card totals.
        {
            id: REFUND_COLUMN_ID_USD,
            name: (
                <>
                    Refund Amount<br /> In USD
                </>
            ),
            // Shown WITHOUT tax now, same as the Refunds tab — the refunded tax has
            // its own column next to this one. Previous tax-inclusive version:
            // selector: row => row.paid_currency === 'CAD' ? '-' : `$ ${formatAmount(row.refund_amount_usd || 0)}`,
            selector: row => row.paid_currency === 'CAD'
                ? '-'
                : `$ ${formatAmount(refundWithoutTax(allTableGross(row, 'USD'), row.tax_charge_usd, row.refund_amount_usd))}`,
            sortable: true,
            width: "125px",
            style: refundCellStyle
        },
        // NEW column: the tax part of the refund, reported on its own.
        // {
        //     name: (
        //         <>
        //             Refund Tax Amount<br /> In USD
        //         </>
        //     ),
        //     selector: row => row.paid_currency === 'CAD'
        //         ? '-'
        //         : `$ ${formatAmount(refundTaxAmount(allTableGross(row, 'USD'), row.tax_charge_usd, row.refund_amount_usd))}`,
        //     sortable: true,
        //     width: "125px",
        //     style: {
        //         backgroundColor: '#dceefb'
        //     }
        // },
        // NEW column: the tax still held on the row (the "Tax Price" above less the
        // refunded tax) — accent-coloured, and the column the "Total Tax Collected
        // after deductions" card totals. See netTaxRetained. As with the net-paid
        // column on this tab, the PAYOUT rows render here too but are not part of
        // that card.
        // {
        //     id: TOTAL_TAX_COLUMN_ID_USD,
        //     name: (
        //         <>
        //             Total Tax<br /> In USD
        //         </>
        //     ),
        //     selector: row => row.paid_currency === 'CAD'
        //         ? '-'
        //         : `$ ${formatAmount(netTaxRetained(allTableGross(row, 'USD'), row.tax_charge_usd, row.refund_amount_usd))}`,
        //     sortable: true,
        //     width: "125px",
        //     style: taxCellStyle
        // },
        // NEW column: "Net Paid (Excl. Tax)" + "Total Tax" (see
        // netPaymentWithTax). USD counterpart, commented out like the USD tax
        // column above it.
        // {
        //     name: (
        //         <>
        //             Net Paid (Incl. Tax)<br /> In USD
        //         </>
        //     ),
        //     selector: row => row.paid_currency === 'CAD'
        //         ? '-'
        //         : `$ ${formatAmount(netPaymentWithTax(allTableGross(row, 'USD'), row.tax_charge_usd, row.refund_amount_usd))}`,
        //     sortable: true,
        //     width: "155px",
        //     style: {
        //         backgroundColor: '#dceefb'
        //     }
        // },
        // NEW column (added alongside the existing ones — nothing above/below changed)
        // Accent-coloured: this is the column the "Total Received" card totals.
        // On this tab the column also renders the PAYOUT rows, which are money
        // going out and are not part of that card — see the card's note.
        {
            id: NET_PAID_COLUMN_ID_USD,
            name: (
                <>
                    Net Paid<br /> In USD
                </>
            ),
            // Titled without "(Excl. Tax)" - no USD tax to be exclusive of.
            selector: row => row.paid_currency === 'CAD'
                ? '-'
                : `$ ${formatAmount(netAmountPaidByLearner(allTableGross(row, 'USD'), row.tax_charge_usd, row.refund_amount_usd))}`,
            sortable: true,
            width: "155px",
            style: netPaidCellStyle
        },
        {
            name: (
                <>
                    Teacher Earning<br /> in USD
                </>
            ),
            // Same change as the Payment Received tab: derived from the net
            // (post-refund) amount the learner paid. Previous gross-amount version:
            // selector: row => row.paid_currency === 'CAD' ? '-' : `$ ${formatAmount(row.teacher_earning_usd)}`,
            selector: row => row.paid_currency === 'CAD'
                ? '-'
                : `$ ${formatAmount(netEarning(row.teacher_earning_usd, allTableGross(row, 'USD'), row.tax_charge_usd, row.refund_amount_usd))}`,
            sortable: true,
            width: "125px",
            style: {
                backgroundColor: '#dceefb'
            }
        },
        // Plain (no accent) since the card stopped being this column's total — it
        // is now the total of "Admin Earnings After Deducting Fees", two columns along. The
        // PAYOUT rows render here as 0.00 (admin_earning is 0 on a payout) and are
        // not part of that card either way.
        {
            id: ADMIN_EARNING_COLUMN_ID_USD,
            name: (
                <>
                    Admin Earning<br /> in USD
                </>
            ),
            // Now derived from the new "Net Paid (Excl. Tax)" column (refunds
            // deducted) instead of the gross amount — see netEarning above.
            // Previous gross-amount version, kept for reference:
            // selector: row => row.paid_currency === 'CAD' ? '-' : `$ ${formatAmount(row.admin_earning_usd)}`,
            selector: row => row.paid_currency === 'CAD'
                ? '-'
                : `$ ${formatAmount(netEarning(row.admin_earning_usd, allTableGross(row, 'USD'), row.tax_charge_usd, row.refund_amount_usd))}`,
            sortable: true,
            width: "125px",
            // No accent tint any more — the revenue accent moved to the
            // "Admin Earnings After Deducting Fees" column, which is what the "Total
            // Revenue" card is the total of. Previous version:
            //     style: adminEarningCellStyle
        },
        // NEW column: the ACTUAL Stripe processing fee on this transaction — see
        // feeCell. Placed right after "Admin Earning" because
        // the "Total Revenue" card is that column's total LESS this one's.
        //
        // This tab mixes the two legs, and only the received leg carries a fee:
        // the PAYOUT rows render "-" here (feeCell), because Stripe's cut was
        // taken on the charge coming in and is already reported on the received
        // row for that very same transaction.
        {
            id: FEE_COLUMN_ID_USD,
            name: (
                <>
                    Fees<br /> in USD
                </>
            ),
            selector: row => feeCell(row, 'USD'),
            sortable: true,
            width: "125px"
        },
        // NEW column: the two columns before it subtracted — Admin Earning less
        // the actual Stripe fee. Accent-coloured: this is the column the "Total
        // Revenue" card totals. The PAYOUT rows render 0.00 here (their Admin
        // Earning is 0.00 and they carry no fee) and are not part of that card
        // either way. See adminEarningAfterFee.
        {
            id: ADMIN_EARNING_AFTER_FEE_COLUMN_ID_USD,
            name: (
                <>
                    Admin Earnings After Deducting Fees<br /> in USD
                </>
            ),
            selector: row => adminEarningAfterFeeCell(row, 'USD', allTableGross(row, 'USD')),
            sortable: true,
            width: "140px",
            style: adminEarningAfterFeeCellStyle
        },
        {
            name: (
                <>
                    Class Price<br /> In CAD
                </>
            ),
            selector: row => row.paid_currency === 'CAD' ? `$ ${formatAmount(row.class_price_cad || 0)}` : '-',
            sortable: true,
            width: "100px",
            style: {
                backgroundColor: '#ffffe0'
            }
        },
        {
            name: (
                <>
                    Class Price Before Tax<br /> In CAD
                </>
            ),
            selector: row => row.paid_currency === 'CAD' ? `$ ${formatAmount((row.payment_status === "paid" ? toAmount(row.teacher_amount_cad) : toAmount(row.total_amount_cad)) - toAmount(row.tax_charge_cad))}` : '-',
            sortable: true,
            width: "125px",
            style: {
                backgroundColor: '#ffffe0'
            }
        },
        {
            name: (
                <>
                    Tax Price<br /> In CAD
                </>
            ),
            selector: row => row.paid_currency === 'CAD' ? `$ ${formatAmount(row.tax_charge_cad)}` : '-',
            sortable: true,
            width: "125px",
            style: {
                backgroundColor: '#ffffe0'
            }
        },
        {
            name: (
                <>
                    Class Price After Tax<br /> In CAD
                </>
            ),
            selector: row => row.paid_currency === 'CAD' ? `$ ${formatAmount(row.payment_status === "paid" ? row.teacher_amount_cad : row.total_amount_cad)}` : '-',
            sortable: true,
            width: "155px",
            style: {
                backgroundColor: '#ffffe0'
            }
        },
        // Accent-coloured: this is the column the "Total Refund Provided" card totals.
        {
            id: REFUND_COLUMN_ID_CAD,
            name: (
                <>
                    Refund Amount<br /> In CAD
                </>
            ),
            // Shown WITHOUT tax now — see the USD column above and refundWithoutTax.
            // Previous tax-inclusive version:
            // selector: row => row.paid_currency === 'CAD' ? `$ ${formatAmount(row.refund_amount_cad || 0)}` : '-',
            selector: row => row.paid_currency === 'CAD'
                ? `$ ${formatAmount(refundWithoutTax(allTableGross(row, 'CAD'), row.tax_charge_cad, row.refund_amount_cad))}`
                : '-',
            sortable: true,
            width: "125px",
            style: refundCellStyle
        },
        // NEW column: the tax part of the refund (CAD side of the pair above).
        {
            name: (
                <>
                    Refund Tax Amount<br /> In CAD
                </>
            ),
            selector: row => row.paid_currency === 'CAD'
                ? `$ ${formatAmount(refundTaxAmount(allTableGross(row, 'CAD'), row.tax_charge_cad, row.refund_amount_cad))}`
                : '-',
            sortable: true,
            width: "125px",
            style: {
                backgroundColor: '#ffffe0'
            }
        },
        // NEW column: the tax still held on the row (the "Tax Price" above less the
        // refunded tax in the column beside it) — accent-coloured, and the column the
        // "Total Tax Collected after deductions" card totals. See netTaxRetained. As
        // with the net-paid column on this tab, the PAYOUT rows render here too but
        // are not part of that card.
        {
            id: NET_PAID_COLUMN_ID_CAD,
            name: (
                <>
                    Net Paid (Excl. Tax)<br /> In CAD
                </>
            ),
            selector: row => row.paid_currency === 'CAD'
                ? `$ ${formatAmount(netAmountPaidByLearner(allTableGross(row, 'CAD'), row.tax_charge_cad, row.refund_amount_cad))}`
                : '-',
            sortable: true,
            width: "155px",
            style: netPaidCellStyle
        },
        {
            id: TOTAL_TAX_COLUMN_ID_CAD,
            name: (
                <>
                    Total Tax<br /> In CAD
                </>
            ),
            selector: row => row.paid_currency === 'CAD'
                ? `$ ${formatAmount(netTaxRetained(allTableGross(row, 'CAD'), row.tax_charge_cad, row.refund_amount_cad))}`
                : '-',
            sortable: true,
            width: "125px",
            style: taxCellStyle
        },
        // NEW column: "Net Paid (Excl. Tax)" + "Total Tax" — the whole
        // tax-inclusive amount left with the platform for the row. See
        // netPaymentWithTax. No accent colour: no balance card totals it.
        {
            name: (
                <>
                    Net Paid (Incl. Tax)<br /> In CAD
                </>
            ),
            selector: row => row.paid_currency === 'CAD'
                ? `$ ${formatAmount(netPaymentWithTax(allTableGross(row, 'CAD'), row.tax_charge_cad, row.refund_amount_cad))}`
                : '-',
            sortable: true,
            width: "155px",
            style: {
                backgroundColor: '#ffffe0'
            }
        },
        // NEW column (added alongside the existing ones — nothing above/below changed)
        // Accent-coloured: this is the column the "Total Received" card totals.
        // On this tab the column also renders the PAYOUT rows, which are money
        // going out and are not part of that card — see the card's note.

        {
            name: (
                <>
                    Teacher Earning<br /> in CAD
                </>
            ),
            // Same change as the Payment Received tab: derived from the net
            // (post-refund) amount the learner paid. Previous gross-amount version:
            // selector: row => row.paid_currency === 'CAD' ? `$ ${formatAmount(row.teacher_earning_cad)}` : '-',
            selector: row => row.paid_currency === 'CAD'
                ? `$ ${formatAmount(netEarning(row.teacher_earning_cad, allTableGross(row, 'CAD'), row.tax_charge_cad, row.refund_amount_cad))}`
                : '-',
            sortable: true,
            width: "125px",
            style: {
                backgroundColor: '#ffffe0'
            }
        },
        // Plain (no accent) since the card stopped being this column's total — it
        // is now the total of "Admin Earnings After Deducting Fees", two columns along. The
        // PAYOUT rows render here as 0.00 (admin_earning is 0 on a payout) and are
        // not part of that card either way.
        {
            id: ADMIN_EARNING_COLUMN_ID_CAD,
            name: (
                <>
                    Admin Earning<br /> in CAD
                </>
            ),
            // Now derived from the new "Net Paid (Excl. Tax)" column (refunds
            // deducted) instead of the gross amount — see netEarning above.
            // Previous gross-amount version, kept for reference:
            // selector: row => row.paid_currency === 'CAD' ? `$ ${formatAmount(row.admin_earning_cad)}` : '-',
            selector: row => row.paid_currency === 'CAD'
                ? `$ ${formatAmount(netEarning(row.admin_earning_cad, allTableGross(row, 'CAD'), row.tax_charge_cad, row.refund_amount_cad))}`
                : '-',
            sortable: true,
            width: "125px",
            // No accent tint any more — the revenue accent moved to the
            // "Admin Earnings After Deducting Fees" column, which is what the "Total
            // Revenue" card is the total of. Previous version:
            //     style: adminEarningCellStyle
        },
        // NEW column: the CAD side of the same actual Stripe fee — see feeCell.
        {
            id: FEE_COLUMN_ID_CAD,
            name: (
                <>
                    Fees<br /> in CAD
                </>
            ),
            selector: row => feeCell(row, 'CAD'),
            sortable: true,
            width: "125px"
        },
        // NEW column: the CAD side of the same subtraction — see the USD column
        // above and adminEarningAfterFee.
        {
            id: ADMIN_EARNING_AFTER_FEE_COLUMN_ID_CAD,
            name: (
                <>
                    Admin Earnings After Deducting Fees<br /> in CAD
                </>
            ),
            selector: row => adminEarningAfterFeeCell(row, 'CAD', allTableGross(row, 'CAD')),
            sortable: true,
            width: "140px",
            style: adminEarningAfterFeeCellStyle
        },
        {
            name: 'Refund Date',
            selector: row => {
                if (row.payment_status !== 'refunded' || !row.refund_date) return '-';
                const date = new Date(row.refund_date);
                const day = date.getDate();
                const month = date.getMonth() + 1;
                const formattedMonth = month < 10 ? `0${month}` : month;
                const formattedDay = day < 10 ? `0${day}` : day;
                const year = date.getFullYear();
                return `${formattedDay}-${formattedMonth}-${year}`;
            },
            sortable: true,
            width: "120px"
        },
        {
            name: 'Status',
            selector: row => {
                let buttonStyle = { backgroundColor: "#6c757d", color: "#fff" };
                let buttonText = row.payment_status;

                if (row.payment_status === "paid") {
                    buttonStyle = { backgroundColor: "#0dcaf0", color: "#000" };
                } else if (row.payment_status === "succeeded") {
                    buttonStyle = { backgroundColor: "#198754", color: "#fff" };
                } else if (row.payment_status === "refunded") {
                    buttonStyle = { backgroundColor: "#fd7e14", color: "#fff" };
                } else if (row.payment_status === "cancelled" || row.payment_status === "canceled") {
                    buttonStyle = { backgroundColor: "#dc3545", color: "#fff" };
                }

                return (
                    <button className="border-0 px-1 fw-500" style={{ ...buttonStyle, width: "80px", cursor: "default" }}>
                        {buttonText}
                    </button>
                );
            },
            sortable: true,
            width: "115px"
        },
        // {
        //     name: 'Refunded',
        //     selector: row => row.payment_status === "refunded" ? "Refunded" : "-",
        //     cell: row => (
        //         row.payment_status === "refunded" ? (
        //             <button className="border-0 px-1 fw-500" style={{ backgroundColor: "#fd7e14", color: "#fff", width: "80px", cursor: "default" }}>
        //                 Refunded
        //             </button>
        //         ) : (
        //             <span>-</span>
        //         )
        //     ),
        //     sortable: true,
        //     width: "115px"
        // },

        // {
        //     name: 'Refund Id',
        //     selector: row => row.refund_id ? row.refund_id : '-',
        //     sortable: true,
        //     width: "260px"
        // },
        {
            name: 'Transaction Id',
            selector: row => row.transaction_id,
            sortable: true,
            width: "250px"
        },
        // {
        //     name: 'Receipt',
        //     width: "80px",
        //     selector: row => row.id,
        //     center: true,
        //     cell: (row) => <>
        //         <button type="button" className="btn" style={{ boxShadow: 'none' }} id={row.id} ><a className="material-icons"
        //             onClick={() => {
        //                 const idToSend = (row.learner_id == null || row.learner_id === "") ? row.user_id : row.learner_id;
        //                 const payload = {
        //                     activity_id: row.activity_id.toString(),
        //                     activity_type: row.activity_type,
        //                     ...(row.learner_id ? { learnerId: row.learner_id } : { userId: row.user_id })
        //                 };
        //                 downloadPdf(payload);
        //             }}

        //             id={row.id} >  <svg width={30} height={30} viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
        //                 <path d="M15 0C12.0333 0 9.13319 0.879735 6.66645 2.52796C4.19972 4.17618 2.27713 6.51886 1.14181 9.25975C0.00649929 12.0006 -0.290551 15.0166 0.288228 17.9264C0.867006 20.8361 2.29562 23.5088 4.39341 25.6066C6.49119 27.7044 9.16394 29.133 12.0737 29.7118C14.9834 30.2906 17.9994 29.9935 20.7403 28.8582C23.4812 27.7229 25.8238 25.8003 27.4721 23.3336C29.1203 20.8668 30 17.9667 30 15C30 11.0218 28.4197 7.20644 25.6066 4.3934C22.7936 1.58035 18.9783 0 15 0ZM19.9206 24.6625H10.0794C9.74786 24.6625 9.42992 24.5308 9.1955 24.2964C8.96108 24.062 8.82938 23.744 8.82938 23.4125C8.82938 23.081 8.96108 22.763 9.1955 22.5286C9.42992 22.2942 9.74786 22.1625 10.0794 22.1625H19.9206C20.2522 22.1625 20.5701 22.2942 20.8045 22.5286C21.0389 22.763 21.1706 23.081 21.1706 23.4125C21.1706 23.744 21.0389 24.062 20.8045 24.2964C20.5701 24.5308 20.2522 24.6625 19.9206 24.6625ZM20.8044 15.26L15.88 20.1875C15.6456 20.4218 15.3277 20.5535 14.9963 20.5535C14.6648 20.5535 14.3469 20.4218 14.1125 20.1875L9.19563 15.2713C8.96075 15.0369 8.8286 14.7188 8.82824 14.387C8.82789 14.0551 8.95937 13.7368 9.19376 13.5019C9.42814 13.267 9.74623 13.1348 10.0781 13.1345C10.4099 13.1341 10.7283 13.2656 10.9631 13.5L13.75 16.2869V5.22813C13.75 4.8966 13.8817 4.57866 14.1161 4.34424C14.3505 4.10982 14.6685 3.97813 15 3.97813C15.3315 3.97813 15.6495 4.10982 15.8839 4.34424C16.1183 4.57866 16.25 4.8966 16.25 5.22813V16.2794L19.0369 13.4925C19.2726 13.2648 19.5884 13.1388 19.9161 13.1417C20.2439 13.1445 20.5574 13.276 20.7892 13.5077C21.0209 13.7395 21.1524 14.053 21.1552 14.3807C21.1581 14.7085 21.0321 15.0242 20.8044 15.26Z" fill="#570861" />
        //             </svg></a> </button></>
        // }
    ]

    const creditsColumns = [
        {
            name: 'No',
            selector: row => row.id,
            sortable: false,
            width: "300px"
        },
        // NEW: "Class Date" — the date of the CLASS / MEETING this row is
        // about, which is not the same thing as the payment date beside it: a
        // subscription paid for in January can be a session held in March.
        // A referral credit is earned on an activity rather than on an enrollment,
        // so its class date is that activity's first meeting.
        // The API supplies it as `class_date`, already dd/mm/YYYY. A row with no
        // meeting behind it (offline course, quiz) has no class date and shows
        // "-" — that is the data, not a lookup failure.
        {
            name: 'Class Date',
            // A Multi-Day row prints its span ("first - last") rather than one
            // date; see classDateText. Old single-date selector:
            // selector: row => row.class_date ? row.class_date : '-',
            // cellExport: row => row.class_date ? row.class_date : '-',
            selector: row => classDateText(row),
            cellExport: row => classDateText(row),
            sortable: true,
            // A credit on a Multi-Day class prints a RANGE here; wrap so it is
            // never clipped to "17/08/2026 - _".
            wrap: true,
            // width: "200px"
            width: "200px"
        },
        // NEW: "Total Meetings" — how many meetings this row is for. An ongoing
        // row is billed per meeting and counts 1; a One-Time / Multi-Day purchase
        // counts every meeting it bought. Rows with none (offline course, quiz,
        // referral credit) show "-". See totalMeetingsText.
        {
            name: 'Total Meetings',
            selector: row => totalMeetingsText(row),
            cellExport: row => totalMeetingsText(row),
            sortable: true,
            width: "130px"
        },
        {
            name: 'Referer',
            selector: row => row.referer,
            sortable: true,
            width: "400px",
            cell: row => {
                if (row.referer_category === 'below_18' ||
                    row.referer_category === 'above_18' ||
                    row.referer_category === 'after_school') {
                    return (
                        <span
                            onClick={() => navigate('/user_details', { state: { id: row.referer_id } })}
                            style={{ color: "rgb(87, 8, 97)", border: "none", background: "none" }}
                        >
                            {row.referer}
                        </span>
                    );
                } else if (row.referer_category == 'teacher') {
                    return (
                        <span
                            onClick={() => navigate('/teacher_details', { state: { teacherId: row.referer_id } })}
                            style={{ color: "rgb(87, 8, 97)", border: "none", background: "none", cursor: 'pointer' }}
                        >
                            {row.referer}
                        </span>
                    )
                } else if (row.referer_category == 'organization') {
                    return (
                        <span
                            onClick={() => navigate('/org_details', { state: { userId: row.referer_id } })}
                            style={{ color: "rgb(87, 8, 97)", border: "none", background: "none", cursor: 'pointer' }}
                        >
                            {row.referer}
                        </span>
                    )
                } else if (row.referer_category == 'org_teacher') {
                    return (
                        <span
                            onClick={() => navigate('/org_teacher_details', { state: { userID: row.referer_id } })}
                            style={{ color: "rgb(87, 8, 97)", border: "none", background: "none", cursor: 'pointer' }}
                        >
                            {row.referer}
                        </span>
                    )
                } else {
                    return (
                        <span>{row.referer}</span>
                    )
                }
            }
        },

        {
            name: 'Referer Role',
            selector: row => row.referer_role,
            sortable: true,
            width: "400px",
            cell: row => {
                if (row.referer_category === 'below_18' ||
                    row.referer_category === 'above_18' ||
                    row.referer_category === 'after_school') {
                    return (
                        <span
                            onClick={() => navigate('/user_details', { state: { id: row.referer_id } })}
                            style={{ color: "rgb(87, 8, 97)", border: "none", background: "none" }}
                        >
                            {row.referer_role}
                        </span>
                    );
                } else if (row.referer_category == 'teacher') {
                    return (
                        <span
                            onClick={() => navigate('/teacher_details', { state: { teacherId: row.referer_id } })}
                            style={{ color: "rgb(87, 8, 97)", border: "none", background: "none", cursor: 'pointer' }}
                        >
                            {row.referer_role}
                        </span>
                    )
                } else if (row.referer_category == 'organization') {
                    return (
                        <span
                            onClick={() => navigate('/org_details', { state: { userId: row.referer_id } })}
                            style={{ color: "rgb(87, 8, 97)", border: "none", background: "none", cursor: 'pointer' }}
                        >
                            {row.referer_role}
                        </span>
                    )
                } else if (row.referer_category == 'org_teacher') {
                    return (
                        <span
                            onClick={() => navigate('/org_teacher_details', { state: { userID: row.referer_id } })}
                            style={{ color: "rgb(87, 8, 97)", border: "none", background: "none", cursor: 'pointer' }}
                        >
                            {row.referer_role}
                        </span>
                    )
                } else {
                    return (
                        <span>{row.referer_role}</span>
                    )
                }
            }
        },


        {
            name: 'Referee',
            selector: row => row.referee,
            sortable: true,
            width: "400px",
            cell: row => {
                if (row.refree_category === 'below_18' ||
                    row.refree_category === 'above_18' ||
                    row.refree_category === 'after_school') {
                    return (
                        <span
                            onClick={() => navigate('/user_details', { state: { id: row.referee_id } })}
                            style={{ color: "rgb(87, 8, 97)", border: "none", background: "none" }}
                        >
                            {row.referee}
                        </span>
                    );
                } else if (row.refree_category == 'teacher') {
                    return (
                        <span
                            onClick={() => navigate('/teacher_details', { state: { teacherId: row.referee_id } })}
                            style={{ color: "rgb(87, 8, 97)", border: "none", background: "none", cursor: 'pointer' }}
                        >
                            {row.referee}
                        </span>
                    )
                } else if (row.refree_category == 'organization') {
                    return (
                        <span
                            onClick={() => navigate('/org_details', { state: { userId: row.referee_id } })}
                            style={{ color: "rgb(87, 8, 97)", border: "none", background: "none", cursor: 'pointer' }}
                        >
                            {row.referee}
                        </span>
                    )
                } else if (row.refree_category == 'org_teacher') {
                    return (
                        <span
                            onClick={() => navigate('/org_teacher_details', { state: { userID: row.referee_id } })}
                            style={{ color: "rgb(87, 8, 97)", border: "none", background: "none", cursor: 'pointer' }}
                        >
                            {row.referee}
                        </span>
                    )
                } else {
                    return (
                        <span>{row.referee}</span>
                    )
                }
            }
        },
        {
            name: 'Referee Role',
            selector: row => row.referee_role,
            sortable: true,
            width: "400px",
            cell: row => {
                if (row.refree_category === 'below_18' ||
                    row.refree_category === 'above_18' ||
                    row.refree_category === 'after_school') {
                    return (
                        <span
                            onClick={() => navigate('/user_details', { state: { id: row.referee_id } })}
                            style={{ color: "rgb(87, 8, 97)", border: "none", background: "none" }}
                        >
                            {row.referee_role}
                        </span>
                    );
                } else if (row.refree_category == 'teacher') {
                    return (
                        <span
                            onClick={() => navigate('/teacher_details', { state: { teacherId: row.referee_id } })}
                            style={{ color: "rgb(87, 8, 97)", border: "none", background: "none", cursor: 'pointer' }}
                        >
                            {row.referee_role}
                        </span>
                    )
                } else if (row.refree_category == 'organization') {
                    return (
                        <span
                            onClick={() => navigate('/org_details', { state: { userId: row.referee_id } })}
                            style={{ color: "rgb(87, 8, 97)", border: "none", background: "none", cursor: 'pointer' }}
                        >
                            {row.referee_role}
                        </span>
                    )
                } else if (row.refree_category == 'org_teacher') {
                    return (
                        <span
                            onClick={() => navigate('/org_teacher_details', { state: { userID: row.referee_id } })}
                            style={{ color: "rgb(87, 8, 97)", border: "none", background: "none", cursor: 'pointer' }}
                        >
                            {row.referee_role}
                        </span>
                    )
                } else {
                    return (
                        <span>{row.referee_role}</span>
                    )
                }
            }
        },
        {
            name: 'Amount in CAD',
            selector: row => `$ ${formatAmount(row.amount_cad)}`,
            sortable: true,
            width: "400px",
            style: {
                backgroundColor: '#ffffe0'
            }
        },
        {
            name: 'Amount in USD',
            selector: row => `$ ${formatAmount(row.amount)}`,
            sortable: true,
            width: "400px",
            style: {
                backgroundColor: '#dceefb'
            }
        },
        {
            name: 'Referral Status',
            selector: row => (
                <button
                    className={`border-0 text-light fw-500 ${row.is_valid === 1 ? 'bg-success' : 'bg-danger'}`}
                    style={{ width: "80px", cursor: "default" }}
                >
                    {row.is_valid === 1 ? 'Valid' : 'Not Valid'}
                </button>
            ),
            sortable: true,
            width: "150px",
            style: {
                backgroundColor: '#ffffe0'
            }
        }
    ]

    const handleBack = () => {
        navigate(-1);
    }

    const [balanceData, setBalanceData] = useState([]);

    const GetBalanceData = () => {
        // The cards are summed from the rows the current filters select, so
        // every filter the table honours has to be sent here too — otherwise
        // the totals describe a different set of rows than the one on screen.
        const requestData = { mode: totalsModeByFilter[selectedFilterOption.value] || 'all' };

        if (startDate && endDate) {
            requestData.startDate = startDate;
            requestData.endDate = endDate;
        }
        if (currency !== 'all') {
            requestData.currency = currency;
        }
        if (taxFilter !== 'all') {
            requestData.taxFilter = taxFilter;
        }
        if (search) {
            requestData.search = search;
        }
        if (selectedTeacherOrg) {
            requestData.teacherId = selectedTeacherOrg.value;
        }
        if (selectedLearner) {
            requestData.learnerId = selectedLearner.value;
        }

        ewalletTotals(requestData).then((response) => {
            if (response.code == 1) {
                setBalanceData(response.data)
            } else {
                setBalanceData([])
            }
        })
    }

    useEffect(() => {
        GetBalanceData()
    }, [startDate, endDate, currency, taxFilter, debounceSearch, selectedTeacherOrg, selectedLearner, selectedFilterOption])


    return (
        <>
            <JqueryLoad />
            <Hdashboard />
            <Helmet>
                <style>
                    {`
                    .card-container {
                    border: 1px solid #e1dfdf;
                    box-shadow: rgba(0, 0, 0, 0.20) 0px 2px 5px;
                    border-radius: 10px;
                    background-color: #570861;
                    color: white;
                    transition: transform 0.3s ease, box-shadow 0.3s ease;
                    }

                    .card-container:hover {
                    transform: translateY(-5px);
                    box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
                    }

                    .card-content {
                    transition: color 0.3s ease;
                    }

                    .card-container:hover .card-content {
                    color: #ffffffcc; /* Lighter shade of white */
                    }

                    /* The sidebar nav is position:fixed to the viewport, so if this
                       page's own content ever scrolls the document horizontally (the
                       ~35-column "All Transactions" table is far wider than the
                       viewport), the nav stays pinned in place while the cards /
                       filters / table slide left underneath it. Containing the
                       horizontal overflow here and letting only the table's own
                       wrapper scroll keeps the sidebar clear of the data. */
                    .dashboard-app {
                    overflow-x: hidden;
                    max-width: 100%;
                    }

                    #download_earnings {
                    overflow-x: auto;
                    max-width: 100%;
                    border-radius: 8px;
                    border: 1px solid #e1dfdf;
                    }

                    .ewallet-filters-row {
                    flex-wrap: wrap;
                    row-gap: 12px;
                    }

                    #download_earnings .rdt_TableHeadRow {
                    background-color: #570861;
                    }

                    #download_earnings .rdt_TableCol,
                    #download_earnings .rdt_TableCol_Sortable {
                    color: #ffffff;
                    font-weight: 600;
                    }

                    #download_earnings .rdt_TableRow:hover {
                    background-color: rgba(87, 8, 97, 0.06);
                    }

                    /* ------------------------------------------------------------
                       "Total Received" <-> "Net Paid (Excl. Tax)".
                       The card is the total of that column, so both wear the same
                       accent colour (NET_PAID_ACCENT) and nothing else on the page
                       does. The card and the column HEADING take the solid shade;
                       the column's cells take the light tint of the same hue, set
                       per column via netPaidCellStyle (react-data-table-component
                       applies a column's style key to its cells only, which is
                       why the heading is reached from here instead).
                       ------------------------------------------------------------ */
                    .card-container.card-accent-net-paid {
                    background-color: ${NET_PAID_ACCENT};
                    }

                    #download_earnings .rdt_TableCol[data-column-id="${NET_PAID_COLUMN_ID_USD}"],
                    #download_earnings .rdt_TableCol[data-column-id="${NET_PAID_COLUMN_ID_CAD}"] {
                    background-color: ${NET_PAID_ACCENT};
                    }

                    /* ------------------------------------------------------------
                       "Total Tax Collected after deductions" <-> the "Total Tax"
                       column it is the total of. Same treatment as the pair above,
                       in a second hue, so the two derivations never read as one.
                       "Tax Price" and "Refund Tax Amount", which that column is
                       worked out from, keep the ordinary currency palette.
                       ------------------------------------------------------------ */
                    .card-container.card-accent-tax {
                    background-color: ${TAX_ACCENT};
                    }

                    #download_earnings .rdt_TableCol[data-column-id="${TOTAL_TAX_COLUMN_ID_USD}"],
                    #download_earnings .rdt_TableCol[data-column-id="${TOTAL_TAX_COLUMN_ID_CAD}"] {
                    background-color: ${TAX_ACCENT};
                    }

                    /* ------------------------------------------------------------
                       "Total Refund Provided" <-> the "Refund Amount" column it is
                       the total of. Third hue, same treatment as the two pairs
                       above. Both are before tax — the refunded tax sits in its own
                       "Refund Tax Amount" column and is not in either figure.
                       ------------------------------------------------------------ */
                    .card-container.card-accent-refund {
                    background-color: ${REFUND_ACCENT};
                    }

                    #download_earnings .rdt_TableCol[data-column-id="${REFUND_COLUMN_ID_USD}"],
                    #download_earnings .rdt_TableCol[data-column-id="${REFUND_COLUMN_ID_CAD}"] {
                    background-color: ${REFUND_ACCENT};
                    }

                    /* ------------------------------------------------------------
                       "Total Revenue" <-> the "Admin Earnings After Deducting Fees" column it is
                       the total of. Fourth and last hue of the card <-> column
                       scheme.

                       Previous version: this rule named the two "Admin Earning"
                       columns (ADMIN_EARNING_COLUMN_ID_USD / _CAD) instead. Those
                       columns render plain now — the accent follows the card, and
                       the card is the total of the subtraction, not of the earning
                       before the fee comes off it.
                       ------------------------------------------------------------ */
                    .card-container.card-accent-revenue {
                    background-color: ${REVENUE_ACCENT};
                    }

                    #download_earnings .rdt_TableCol[data-column-id="${ADMIN_EARNING_AFTER_FEE_COLUMN_ID_USD}"],
                    #download_earnings .rdt_TableCol[data-column-id="${ADMIN_EARNING_AFTER_FEE_COLUMN_ID_CAD}"] {
                    background-color: ${REVENUE_ACCENT};
                    }

                    /* ------------------------------------------------------------
                       "Fees" — the ACTUAL Stripe processing fee per transaction —
                       has NO accent rule here on purpose. It is deliberately not a
                       fifth card <-> column pair: no card is the total of it, it is
                       the middle term of the subtraction the three columns spell
                       out (Admin Earnings After Deducting Fees = Admin Earnings After Deducting Fees = Total
                       Revenue), and the accent belongs to the column holding the
                       result. Heading and cells both render plain, like any
                       ordinary column.

                       Previous version — heading tinted a mid-green of the revenue
                       family (FEE_ACCENT, '#2f7d45'):
                         #download_earnings .rdt_TableCol[data-column-id="stripe_fee_usd"],
                         #download_earnings .rdt_TableCol[data-column-id="stripe_fee_cad"] {
                         background-color: #2f7d45;
                         }
                       ------------------------------------------------------------ */
                `}
                </style>
            </Helmet>
            <main>
                <div className="dashboard-app d-lg-flex px-5 mt-1">
                    {/* <div className="px-4 mt-2"> */}

                    <header className="dashboard-toolbar d-lg-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                            <a className="menu-toggle align-items-center d-lg-none d-flex">
                                <i className="fa fa-bars" />
                            </a>
                            <a href="index.php" className="d-lg-none d-block brand-logo">
                                <img src="./assets/images/logo.png" alt="logo" />
                            </a>
                        </div>
                    </header>

                    <span onClick={handleBack} className="mt-3">
                        <svg width="10" height="18" viewBox="0 0 10 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9.86172 2.225L8.37839 0.75L0.136719 9L8.38672 17.25L9.86172 15.775L3.08672 9L9.86172 2.225Z" fill="#131313" ></path>
                        </svg>
                        <span className="ms-2">Back</span>
                    </span>

                    <div className="d-flex justify-content-between title mt-3 mb-2">
                        <p className="fs-3 fw-500">E Wallet</p>
                        <div>
                            <button onClick={() => navigate("/refund_listing")} className="btn-theme bg-yellow m-2">Refund Requests</button>
                            <button onClick={() => navigate("/meeting_refund_listing")} className="btn-theme bg-yellow m-2">Refunds History</button>
                            {/* <button onClick={() => navigate("/ewallet_balance")} className="btn-theme m-2">E Wallet Balance</button> */}
                        </div>
                    </div>

                    <div className='d-flex flex-wrap gap-3'>
                        {/* Total Received / Total Refund Provided / Total Revenue
                            are split by the currency each transaction was
                            actually charged in (computeEwalletTotals in
                            wallet_model): the USD line counts only USD-charged
                            rows, the CAD line only CAD-charged rows at the
                            amount they were billed at, so neither line mixes the
                            two. Final Total CAD is the CAD line plus the USD
                            line converted at the current USD->CAD rate.
                            ("Total Tax Provided" used to sit beside the tax card
                            and now lives on the Earnings page instead — the
                            backend still returns total_tax_paid, which that page
                            reads.)

                            OLD/STALE NOTE (kept for reference): this used to read
                            "Total Revenue is the total BASE (pre-tax) payment less
                            the base share of any refund — not the platform's
                            commission". That has not been true since the card
                            became an earnings figure: it IS the platform's
                            commission, refund-adjusted per row and now net of the
                            Stripe fee — the total of the "Admin Earnings After Deducting Fees"
                            column. See computeEwalletTotals and the card's own
                            note below. */}
                        {/* TOTAL RECEIVED is the total of the "Net Paid
                            (Excl. Tax)" column, which is what the shared accent
                            colour on the card and that column says — so it is
                            PRE-TAX and already net of refunds, and a fully
                            refunded sale adds 0.00 to it.

                            Two kinds of row are in that column but not in this
                            card: cancelled payments, which never collected any
                            money, and (on the All Transactions tab) the payout
                            rows, which are money going OUT. On the "Payments
                            received" tab the card is the column's exact total. */}
                        <div className='col-lg-2 col-md-3 mt-3 p-4 card-container card-accent-net-paid'>
                            <div className='content card-content'>
                                <p className='fs-5 fw-600'>Total Received</p>
                                <p className='fs-6 fw-500 mt-3'>USD Total: US$ {formatAmount(balanceData?.total_received_usd)}</p>
                                <p className='fs-6 fw-500'>CAD Total: CAD$ {formatAmount(balanceData?.total_received_cad)}</p>
                                <p className='fs-5 fw-600 mt-2'>Final Total : CAD$ {formatAmount(balanceData?.total_received_cad_final)}</p>
                            </div>
                        </div>

                        {/* The total of the "Total Tax" column — the tax charged less
                            the tax refunded, per row — which is what the accent
                            colour the card and that column share says. A fully
                            refunded sale contributes 0.00. Cancelled rows and (on
                            the All Transactions tab) payout rows appear in the
                            column but are not counted here, same as Total Received. */}
                        <div className='col-lg-2 col-md-3 mt-3 p-4 card-container card-accent-tax'>
                            <div className='content card-content'>
                                <p className='fs-5 fw-600'>Total Tax Collected after deductions</p>
                                <p className='fs-6 fw-500 mt-3'>CAD Total: CAD$ {formatAmount(taxCardAmounts().cad)}</p>
                                <p className='fs-5 fw-600 mt-2'>Final Total : CAD$ {formatAmount(taxCardAmounts().final)}</p>
                            </div>
                        </div>

                        {/* The total of the "Refund Amount" column — BEFORE TAX, the
                            same basis that column shows: the stored refund is
                            tax-inclusive, so its tax share is apportioned out first
                            (baseShareOfRefund) and reported by "Refund Tax Amount"
                            instead. Hence the shared accent colour. Unlike the other
                            cards this one does count cancelled rows' refunds — money
                            that went back out counts however the sale ended. */}
                        <div className='col-lg-2 col-md-3 mt-3 p-4 card-container card-accent-refund'>
                            <div className='content card-content'>
                                <p className='fs-5 fw-600'>Total Refund Provided</p>
                                <p className='fs-6 fw-500 mt-3'>USD Total: US$ {formatAmount(balanceData?.total_refund_usd)}</p>
                                <p className='fs-6 fw-500'>CAD Total: CAD$ {formatAmount(balanceData?.total_refund_cad)}</p>
                                <p className='fs-5 fw-600 mt-2'>Final Total : CAD$ {formatAmount(balanceData?.total_refund_cad_final)}</p>
                            </div>
                        </div>

                        {/* The total of the "Admin Earnings After Deducting Fees" column — the
                            platform's own share of each sale, reduced in
                            proportion by any refund, LESS the actual Stripe fee
                            that sale cost. That is the column this card shares an
                            accent colour with:

                                Total Revenue = total "Admin Earnings After Deducting Fees"
                                              = total "Admin Earning" - total "Fees"

                            per currency bucket, each row rounded the way the
                            column prints it, so adding that one column up by hand
                            — on screen or in the downloaded statement — gives the
                            number on this card. No tax is in it (the split is
                            taken on the pre-tax class price).

                            The fee is Stripe's own figure per transaction
                            (tbl_stripe_transaction_fee, api/modules/v1/wallet/
                            stripe_fee.js), never a rate applied here, and is NOT
                            scaled down by a refund: Stripe keeps its processing
                            fee on a refunded charge, so a refunded sale earns
                            nothing and still costs the fee. A charge with no fee
                            recorded yet has nothing deducted for it — the column
                            shows "-" under "Fees" and the card is never reduced by
                            an estimate.

                            The subtraction is done once, server-side, in
                            computeEwalletTotals (total_revenue_* less
                            total_stripe_fee_*), so this card, the balance block on
                            the Excel sheet and the PDF statement all print the
                            same figure.

                            OLD BEHAVIOUR (kept for reference): the card was the
                            "Admin Earning" total with nothing subtracted, and
                            "Admin Earning" was the accent-coloured column. The fee
                            was reported beside it but taken off nothing, so the
                            page never showed what the platform actually kept. It
                            now has a column of its own, and the card is that
                            column's total.

                            Rows that are in the column but NOT in this card:
                            cancelled payments, which never collected any money (and
                            were never charged a fee), and (on the All Transactions
                            tab) payout rows, whose Admin Earning is 0.00 and which
                            carry no fee of their own. Same exclusions as Total
                            Received.

                            Teacher Earning, every payout and every teacher / learner
                            / organization figure are untouched by this card. */}
                        <div className='col-lg-2 col-md-3 mt-3 p-4 card-container card-accent-revenue'>
                            <div className='content card-content'>
                                <p className='fs-5 fw-600'>Total Admin Earnings After Deducting Fees</p>
                                <p className='fs-6 fw-500 mt-3'>USD Total: US$ {formatAmount(balanceData?.total_revenue_usd)}</p>
                                <p className='fs-6 fw-500'>CAD Total: CAD$ {formatAmount(balanceData?.total_revenue_cad)}</p>
                                <p className='fs-5 fw-600 mt-2'>Final Total : CAD$ {formatAmount(balanceData?.total_revenue_cad_final)}</p>
                            </div>
                        </div>
                    </div>

                    <div className="d-flex mt-3 justify-content-start gap-3 ewallet-filters-row">
                        <div className='col-md-2'>
                            <label className="mb-2">Filter :</label>
                            <Select
                                options={[
                                    { value: "all", label: "All Transactions" },
                                    // { value: "payments_received", label: "Payments received" },
                                    // { value: "payments_made", label: "Payments made" },
                                    { value: "refunds", label: "Refunds" },
                                    { value: "credits", label: "Credits" },
                                    // { value: "unaID", label: "UnaID" },
                                    // { value: "payment_hold", label: "Payment Hold" },
                                    // { value: "payment_under_review", label: "Payment Under Review" },
                                    // { value: "payment_denied", label: "Payment Denied" },
                                ]}
                                className="skip_pdf"
                                id="filter-select"
                                value={selectedFilterOption}
                                onChange={handleFilterChange}
                                styles={{
                                    width: "100%",
                                    maxWidth: "200px",
                                    minHeight: "40px"
                                }}
                                placeholder="Select Filter"
                            />
                        </div>

                        <div className="col-md-2">
                            <label htmlFor="daily-start-date" className="mb-2">Start Date:</label>
                            <input
                                type="date"
                                name="start_date"
                                id="daily-start-date"
                                className="form-control"
                                required
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                        </div>

                        <div className="col-md-2">
                            <label htmlFor="daily-end-date" className="mb-2">End Date:</label>
                            <input
                                type="date"
                                name="end_date"
                                id="daily-end-date"
                                className="form-control"
                                required
                                value={endDate}
                                min={startDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </div>

                        <div className="col-md-2">
                            <label htmlFor="currency-select" className="mb-2">Currency:</label>
                            <select
                                id="currency-select"
                                className="form-control"
                                value={currency}
                                onChange={(e) => setCurrency(e.target.value)}
                            >
                                <option value="all">All</option>
                                <option value="CAD">CAD</option>
                                <option value="USD">USD</option>
                            </select>
                        </div>

                        <div className="col-md-2">
                            <label htmlFor="tax-select" className="mb-2">Tax:</label>
                            <select
                                id="tax-select"
                                className="form-control"
                                value={taxFilter}
                                onChange={(e) => setTaxFilter(e.target.value)}
                            >
                                <option value="all">Both</option>
                                <option value="tax">Tax</option>
                                <option value="non_tax">Non Tax</option>
                            </select>
                        </div>

                        <div className='col-md-3'>
                            <label className="mb-2">Teacher / Organization:</label>
                            <Select
                                options={teacherOrgOptions}
                                className="skip_pdf"
                                id="teacher-org-select"
                                value={selectedTeacherOrg}
                                onChange={(option) => setSelectedTeacherOrg(option)}
                                isClearable
                                styles={{
                                    width: "100%",
                                    minHeight: "40px"
                                }}
                                placeholder="All Teachers & Organizations"
                            />
                        </div>

                        <div className='col-md-3'>
                            <label className="mb-2">Learner:</label>
                            <Select
                                options={learnerOptions}
                                className="skip_pdf"
                                id="learner-select"
                                value={selectedLearner}
                                onChange={(option) => setSelectedLearner(option)}
                                isClearable
                                styles={{
                                    width: "100%",
                                    minHeight: "40px"
                                }}
                                placeholder="All Learners"
                            />
                        </div>

                        {/* Both offered on EVERY transaction-type tab whenever
                            that tab has rows to render, whatever narrowed them
                            down — a date range, a teacher, a learner, or nothing
                            at all. Each tab downloads its own table: "Download"
                            asks the API to render it as a PDF (see
                            downloadRouteByFilter), "Excel" writes the SAME
                            statement as a .xlsx from the rows already on screen
                            (see excelSheetByFilter). */}
                        {rowsForFilter(selectedFilterOption.value, {
                            allData,
                            paymentReceivedData,
                            paymentMadeData: data,
                            refundData,
                            creditsData
                        }).length > 0 &&
                            <div className="col-md-3 d-flex align-items-end gap-2">
                                <button className="btn-theme bg-yellow" onClick={() => downloadPdf()}>Download</button>
                                <button className="btn-theme bg-yellow" onClick={() => downloadExcel()}>Excel</button>
                            </div>
                        }

                    </div>

                    <div className="mt-2">
                        <CInputGroup>
                            <CFormInput type="text" placeholder="Search" onChange={(e) => {
                                setTimeout(() => setSearch(e.target.value), 1000);
                            }} />
                            <CButton style={{ backgroundColor: "rgb(87, 8, 97)", color: "light" }}>
                                <FontAwesome className="fa-solid fa-search" name="search" />
                            </CButton>
                        </CInputGroup>
                    </div>

                    <div id='download_earnings'>
                        <div className='mt-1'>

                            {selectedFilterOption.value === 'all' && (
                                <>
                                    {allData && allData.length > 0 ? (
                                        <DataTable columns={AllDataColumns} data={allData} keyField="id" pagination customStyles={customStyles} />
                                    ) : (
                                        <p className="mt-5 text-center text-purple fw-500 fs-3">No data Found</p>
                                    )}
                                </>
                            )}

                            {selectedFilterOption.value === 'payments_made' && (
                                <>
                                    {data && data.length > 0 ? (
                                        <DataTable columns={paymentMadeColumns} data={data} pagination customStyles={customStyles} keyField="id" />
                                    ) : (
                                        <p className="mt-5 text-center text-purple fw-500 fs-3">No data Found</p>
                                    )}
                                </>
                            )}

                            {selectedFilterOption.value === 'refunds' && (
                                <>
                                    {refundData && refundData.length > 0 ? (
                                        <DataTable columns={refundColumns} data={refundData} pagination customStyles={customStyles} keyField="id" />
                                    ) : (
                                        <p className="mt-5 text-center text-purple fw-500 fs-3 mt-5">No data Found</p>
                                    )}
                                </>
                            )}

                            {selectedFilterOption.value === 'payments_received' && (
                                <>
                                    {paymentReceivedData && paymentReceivedData.length > 0 ? (
                                        <DataTable columns={paymentReceivedColumns} data={paymentReceivedData} pagination customStyles={customStyles} keyField="id" />
                                    ) : (
                                        <p className="mt-5 text-center text-purple fw-500 fs-3 mt-5">No data Found</p>
                                    )}
                                </>
                            )}

                            {selectedFilterOption.value === 'credits' && (
                                <>
                                    {creditsData && creditsData.length > 0 ? (
                                        <DataTable columns={creditsColumns} data={creditsData} pagination customStyles={customStyles} keyField="id" />
                                    ) : (
                                        <p className="mt-5 text-center text-purple fw-500 fs-3 mt-5">No data Found</p>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
}
